import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useWindowSize from '../../components/useWindowResize';
import { AppDispatch, RootState } from '../../app/store';
import { useGetBoreholeSummaryQuery } from '../../services/boreholeApi';
import { useImageTypesQuery, useTrackTypesHierarchyQuery } from '../../services/trackTypeApi';
import { getBoreholesByProject } from '../../services/boreholesSlice';
import {
  addGraphTracksToGroups,
  addTadpoleTracksToGroups,
  addTracks,
  addBoreholes,
  getViews,
  addView,
  updateView,
  changeViewLevel,
  setSelectedUserViewId,
  setSelectedClientViewId,
  updateGroupTrackOrder,
  updateGroupOrder,
  setGroupTrackCollapsed,
  setGroupCollapsed,
  updateViewCurves,
  deleteTrack,
  getBoreholeOffsetLines,
  clearView
} from '../../services/viewsSlice';
import {
  getTemplates,
  getTemplate,
  fetchScalesAndTrackData,
  addGraphTracks,
  addCumulativeTrack,
  addTadpoleTrack,
  addImageTracks,
  addLithologyTrack,
  addTextTrack,
  addStratigraphyTrack,
  addNewTemplate,
  addTemplate,
  updateTemplate,
  deleteTemplate,
  changeTemplateLevel,
  clearTrackData,
  clearTrackDataCache,
  clearScaleMetadata,
  clearTemplate,
  mergeDirectTrackData,
  setSelectedBuiltInTemplateId,
  setSelectedUserTemplateId,
  setSelectedBoreholeTemplateId,
  setSelectedProjectTemplateId,
  setSelectedClientTemplateId,
  setSelectedTemplateId,
  setNextTemplateId,
  clearNotSaved,
  updateCurves,
  updateCurve as updateCurveAction,
  setShowGridlines,
  clearUrlTemplateId,
  setScaleType,
  removeTrack,
  addCurve,
  setTrackCollapsed,
  changeDataValue,
  addDataValue,
  deleteDataValue,
  changeTextData,
  changeStratigraphyData,
  updateTrackOrder,
  saveLithologyData,
  saveTextData,
  saveStratigraphyData,
  changeDepth,
  setTrackEditMode,
  addTextValue,
  addStratigraphyValue,
  getMultipleBoreholeTextData,
  getMultipleBoreholeStratigraphyData,
  addStatisticsTracks,
  deleteStatisticsTrack
} from './templateSlice';
import { buildCumulativeGroupsFromTracks, computeOptimalResolutionsForTrackTypes } from './resolutionHelpers';
import TrackDataTable from './TrackDataTable';
import useTemplateLogViewer from './hooks/useTemplateLogViewer';
import useViewLogViewer from './hooks/useViewLogViewer';
import { useBoreholeLogPanels } from './hooks/useBoreholeLogPanels';
import TrackData from '../track-data/TrackData';
import LeftSideMenu from '../../components/side-menu/log-viewer-left-side-menu';
import RightSideMenu from './RightSideMenu';
import RightSidePanel from './RightSidePanel';
import LeftSidePanel from './LeftSidePanel';
import { confirmAlert } from 'react-confirm-alert';
import NotSavedDialog from '../../components/dialogs/not-saved-dialog';
import { fetchViewScalesAndTrackDataDirect } from './viewTrackDataService';
import { getRandomColor } from '../../components/random';
import DepthData from '../../components/panels/log-viewer/depth-data';
import Loader from '../../components/loader';
import './borehole-log-viewer.scss';

let lastSingleScaleRequestKey = '';
let lastMultiScaleRequestKey = '';
let isSingleScaleRequestInFlight = false;
let isMultiScaleRequestInFlight = false;
let lastUnifiedFetchKey = '';
let isUnifiedFetchInFlight = false;

// If returned data extends the depth range, only re-fetch (including scales) when the
// new range grows to at least this multiple of the previously-fetched range.
const DEPTH_REFETCH_GROWTH_FACTOR = 2.0;
const TRACK_TYPES_HIDDEN_IN_LOGGED_DEPTH = new Set([5, 7, 9]);

const BoreholeLogViewerV2 = ({setBorehole}: any) => {
  const { boreholeId, clientId, projectId, templateId, viewId } = useParams<any>();
  const history = useHistory();
  const dispatch = useDispatch<AppDispatch>();
  const windowSize: any = useWindowSize();

  const template = useSelector((state: RootState) => state.templates?.template);
  const templatesLoaded = useSelector((state: RootState) => state.templates?.templatesLoaded);
  const builtInTemplates = useSelector((state: RootState) => state.templates?.builtInTemplates || []);
  const allTemplates = useSelector((state: RootState) => state.templates?.allTemplates || []);
  const templateLoading = useSelector((state: RootState) => state.templates?.templateLoading);
  const scalesLoaded = useSelector((state: RootState) => state.templates?.scalesLoaded);
  const scalesLoading = useSelector((state: RootState) => state.templates?.scalesLoading);
  const trackDataLoading = useSelector((state: RootState) => state.templates?.trackDataLoading);
  const templateMinDepth = useSelector((state: RootState) => state.templates?.minDepth ?? null);
  const templateMaxDepth = useSelector((state: RootState) => state.templates?.maxDepth ?? null);
  const boreholeTextTrackData = useSelector((state: RootState) => state.templates?.boreholeTextTrackData ?? {});
  const boreholeStratigraphyTrackData = useSelector((state: RootState) => state.templates?.boreholeStratigraphyTrackData ?? {});
  const viewBoreholes = useSelector((state: RootState) => state.views?.boreholes || []);
  const trackData = useSelector((state: RootState) => state.templates?.trackData || []);
  const groupTrackData = useSelector((state: RootState) => state.templates?.groupTrackData || []);
  const scaleMetadata = useSelector((state: RootState) => state.templates?.scaleMetadata || []);
  const scaleMetadataByBorehole = useSelector((state: RootState) => state.templates?.scaleMetadataByBorehole || {});
  const trackDataCache = useSelector((state: RootState) => state.templates?.trackDataCache || {});
  const loadingHighResolution = useSelector((state: RootState) => state.templates?.loadingHighResolution);
  const trackImages = useSelector((state: RootState) => state.templates?.trackImages || []);
  const groupTrackImages = useSelector((state: RootState) => state.templates?.groupTrackImages || []);
  const urlTemplateId = useSelector((state: RootState) => state.templates?.urlTemplateId);
  const boreholesByProject = useSelector((state: RootState) => state.borehole?.boreholesByProject || []);
  const viewData = useSelector((state: RootState) => state.views?.view);
  const allViews = useSelector((state: RootState) => state.views?.allViews || []);
  const reduxSelectedViewId = useSelector((state: RootState) => state.views?.selectedViewId);
  const templateLevels = useSelector((state: RootState) => state.templates?.templateLevels || []);
  const selectedTemplateId = useSelector((state: RootState) => state.templates?.selectedTemplateId);
  const templateNotSaved = useSelector((state: RootState) => state.templates?.notSaved);
  const canSaveTemplate = useSelector((state: RootState) => state.templates?.canSaveTemplate);
  const canDeleteTemplate = useSelector((state: RootState) => state.templates?.canDeleteTemplate);
  const templateSaveError = useSelector((state: RootState) => state.templates?.templateSaveError);
  const viewNotSaved = useSelector((state: RootState) => state.views?.notSaved);
  const canSaveView = useSelector((state: RootState) => state.views?.canSaveView);
  const canDeleteView = useSelector((state: RootState) => state.views?.canDeleteView);
  const viewsLoading = useSelector((state: RootState) => state.views?.viewsLoading);
  const viewLoading = useSelector((state: RootState) => state.views?.viewLoading);
  const deletedCurves = useSelector((state: RootState) => state.templates?.deletedCurves);
  const nextTemplateId = useSelector((state: RootState) => state.templates?.nextTemplateId);
  const nextTemplateLevel = useSelector((state: RootState) => state.templates?.nextTemplateLevel);
  const dataNotSaved = useSelector((state: RootState) => state.templates?.dataNotSaved ?? []);
  const { connection, connected } = useSelector((state: RootState) => state.account);

  const { data: trackTypesHierarchyData, isFetching: trackTypesHierarchyLoading } = useTrackTypesHierarchyQuery(
    { boreholeId: Number(boreholeId), clientId, projectId },
    { skip: !clientId || !projectId || !boreholeId, refetchOnMountOrArgChange: true }
  );
  const { data: imageTypes } = useImageTypesQuery(
    { boreholeId, clientId, projectId, imageViewer: false },
    { skip: !clientId || !projectId || !boreholeId, refetchOnMountOrArgChange: true }
  );

  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(templateId ?? null);
  const [depthType, setDepthType] = useState<string>('Borehole');
  const [startDepth, setStartDepth] = useState<number | null>(null);
  const [endDepth, setEndDepth] = useState<number | null>(null);
  const [lastTrackRequest, setLastTrackRequest] = useState<any>(null);
  const [lastMultiTrackRequest, setLastMultiTrackRequest] = useState<any>(null);
  const [lastMultiTrackResponse, setLastMultiTrackResponse] = useState<any>(null);
  const lastTemplateIdRef = useRef<string | null>(null);
  const [selectedTrackTypeIds, setSelectedTrackTypeIds] = useState<number[]>([]);
  const [selectedTemplateTrackType, setSelectedTemplateTrackType] = useState<number>(0);
  const [selectedTrackType, setSelectedTrackType] = useState<number | null>(null);
  const [noDataTrackTypeIds, setNoDataTrackTypeIds] = useState<number[]>([]);
  const [addedTrackTypeIds, setAddedTrackTypeIds] = useState<number[]>([]);
  const [isLocalTemplate, setIsLocalTemplate] = useState<boolean>(false);
  const [showOnlyTrackTypesWithData, setShowOnlyTrackTypesWithData] = useState<boolean>(true);
  const [checkedImageTypes, setCheckedImageTypes] = useState<number[]>([]);
  const [checkedLithologyTypes, setCheckedLithologyTypes] = useState<number[]>([]);
  const [checkedStratigraphyTypes, setCheckedStratigraphyTypes] = useState<number[]>([]);
  const [checkedTextTypes, setCheckedTextTypes] = useState<number[]>([]);
  const [selectedBoreholeIds, setSelectedBoreholeIds] = useState<number[]>([]);
  const [addedBoreholeIds, setAddedBoreholeIds] = useState<number[]>([]);
  const [pendingBoreholeIds, setPendingBoreholeIds] = useState<number[]>([]);
  const [hasConfirmedBoreholes, setHasConfirmedBoreholes] = useState<boolean>(false);
  const [addTrackTargetBoreholeId, setAddTrackTargetBoreholeId] = useState<string>('all');
  const [selectedViewId, setSelectedViewId] = useState<string | null>(viewId ?? null);
  const [showCharts, setShowCharts] = useState<boolean>(true);
  const [selectedChartCategory, setSelectedChartCategory] = useState<any>(null);
  const [, setManualDepthRange] = useState<boolean>(false);
  const [showRightSidePanel, setShowRightSidePanel] = useState<boolean>(false);
  const [showAddTrackPanel, setShowAddTrackPanel] = useState<boolean>(false);
  const [showReorderPanel, setShowReorderPanel] = useState<boolean>(false);
  const [showAdjustmentsPanel, setShowAdjustmentsPanel] = useState<boolean>(false);
  const [showCorrelationPanel, setShowCorrelationPanel] = useState<boolean>(false);
  const [showAddStatisticsPanel, setShowAddStatisticsPanel] = useState<boolean>(false);
  const [selectedTrack, setSelectedTrack] = useState<any>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<any>(null);
  const [showSaveAsModal, setShowSaveAsModal] = useState<boolean>(false);
  const [showSaveViewAsModal, setShowSaveViewAsModal] = useState<boolean>(false);
  const [showNotSavedModal, setShowNotSavedModal] = useState<boolean>(false);
  const [saveTemplateName, setSaveTemplateName] = useState<string>('');
  const [saveViewName, setSaveViewName] = useState<string>('');
  const [saveAsTemplateLevel, setSaveAsTemplateLevel] = useState<any>(1);
  const [saveAsViewLevel, setSaveAsViewLevel] = useState<any>(1);
  const [saveTemplateNameError, setSaveTemplateNameError] = useState<any>(null);
  const [saveViewNameError, setSaveViewNameError] = useState<any>(null);
  const [moveTemplateError, setMoveTemplateError] = useState<any>(null);
  const [moveViewError, setMoveViewError] = useState<any>(null);
  const [mouseWheelZoom, setMouseWheelZoom] = useState<boolean>(true);
  const [showDepthGridLines, setShowDepthGridlines] = useState<boolean>(true);
  const [depthMajorIntervals, setDepthMajorIntervals] = useState<number>(10);
  const [depthMinorIntervals, setDepthMinorIntervals] = useState<number>(2);
  const [showDepthMinorIntervals, setShowDepthMinorIntervals] = useState<boolean>(false);
  const [pauseUpdate, setPauseUpdate] = useState<boolean>(false);

  const isLoggedDepthTypeSelected = useMemo(() => {
    return typeof depthType === 'string' && depthType.toLowerCase().includes('logged');
  }, [depthType]);

  const addTrackAllowedCategories = useMemo(() => {
    if (!isLoggedDepthTypeSelected) return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
    return [0, 1, 2, 3, 4, 6, 8];
  }, [isLoggedDepthTypeSelected]);

  // Direct view-mode data/scales state (bypass redux fetch pipeline)
  const [directGroupTrackData, setDirectGroupTrackData] = useState<any[]>([]);
  const [directScaleMetadataByBorehole, setDirectScaleMetadataByBorehole] = useState<{ [boreholeId: string]: any[] }>({});
  const [directGroupTrackImages, setDirectGroupTrackImages] = useState<any[]>([]);
  const [directScalesLoaded, setDirectScalesLoaded] = useState<boolean>(false);
  const [directLoadingHighResolution, setDirectLoadingHighResolution] = useState<boolean>(false);

  const lastUnifiedFetchRef = useRef<string>('');
  const skipNextUnifiedFetchRef = useRef<boolean>(false);
  const lastViewIdRef = useRef<string | null>(null);
  const initialViewFetchDoneRef = useRef<boolean>(false);
  // Track the depth range used in the last scale-metadata fetch.
  // We skip the getScaleMetadata round-trip when the new range is within [0.5×, 2×]
  // of the last checked range (optimal resolution is unlikely to have changed).
  const lastScaleFetchedRangeRef = useRef<number | null>(null);
  // Holds the full borehole depth extents so handleTemplateSelection can reset the
  // visible range without needing boreholeData/depthType in its dependency array.
  const boreholeFullDepthRef = useRef<{ minDepth: number; maxDepth: number } | null>(null);

  const requestKey = useMemo(() => {
    return JSON.stringify({
      clientId,
      projectId,
      boreholeId,
      templateId,
      viewId
    });
  }, [clientId, projectId, boreholeId, templateId, viewId]);



  // Clear stale track data whenever the borehole/template/view context changes
  // (covers navigating away and back, or switching boreholes)
  useEffect(() => {
    dispatch(clearTrackData());
    dispatch(clearTrackDataCache());
    dispatch(clearScaleMetadata());
    lastUnifiedFetchRef.current = '';
    lastUnifiedFetchKey = '';
    lastSingleScaleRequestKey = '';
    lastMultiScaleRequestKey = '';
    isUnifiedFetchInFlight = false;
    isSingleScaleRequestInFlight = false;
    isMultiScaleRequestInFlight = false;
    lastScaleFetchedRangeRef.current = null; // force full scale re-fetch on next load
  }, [requestKey, dispatch]);

  const viewportHeight = useMemo(() => {
    const height = Number(windowSize?.height || window.innerHeight || 0);
    return Math.max(200, height - 200);
  }, [windowSize]);

  // Sync Redux selectedViewId to local state when view is saved-as.
  // Only apply when the URL already has a viewId — prevents the stale Redux value
  // from re-loading the last view when the user navigates back without one.
  useEffect(() => {
    if (!viewId) return;
    if (reduxSelectedViewId && reduxSelectedViewId !== selectedViewId) {
      setSelectedViewId(String(reduxSelectedViewId));
    }
  }, [viewId, reduxSelectedViewId, selectedViewId]);

  // Clear view and template state when leaving the log viewer so the next entry starts fresh.
  useEffect(() => {
    return () => {
      dispatch(clearView());
      dispatch(clearTemplate());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch text offset data when boreholes or the selected text track type changes
  useEffect(() => {
    if (!clientId || !viewBoreholes?.length || !viewData?.topOffsetTextTrackTypeId) return;
    dispatch(getMultipleBoreholeTextData({
      clientId,
      boreholeIds: viewBoreholes.map((b: any) => b.id),
      trackTypeId: viewData.topOffsetTextTrackTypeId
    }));
  }, [clientId, viewBoreholes, viewData?.topOffsetTextTrackTypeId, dispatch]);

  // Fetch stratigraphy offset data when boreholes or the selected stratigraphy track type changes
  useEffect(() => {
    if (!clientId || !viewBoreholes?.length || !viewData?.topOffsetStratigraphyTrackTypeId) return;
    dispatch(getMultipleBoreholeStratigraphyData({
      clientId,
      boreholeIds: viewBoreholes.map((b: any) => b.id),
      trackTypeId: viewData.topOffsetStratigraphyTrackTypeId
    }));
  }, [clientId, viewBoreholes, viewData?.topOffsetStratigraphyTrackTypeId, dispatch]);

  useEffect(() => {
    if (!clientId || !projectId || !boreholeId) return;
    dispatch(getTemplates({ templateType: 0, clientId, projectId, boreholeId }));
  }, [clientId, projectId, boreholeId, dispatch]);

  useEffect(() => {
    if (!clientId || !projectId) return;
    dispatch(getBoreholesByProject());
  }, [clientId, projectId, dispatch]);

  useEffect(() => {
    dispatch(getViews({ templateType: 0 }));
  }, [dispatch]);

  // Mirror V1 behaviour: once both the URL templateId and allTemplates are available,
  // dispatch setSelectedTemplateId so that canSaveTemplate is initialised correctly.
  useEffect(() => {
    if (templateId && allTemplates?.length) {
      const templateIdNum = +templateId;
      const found = allTemplates.find((t: any) => t.id == templateIdNum);
      if (!found) return;
      dispatch(setSelectedTemplateId(templateIdNum));
    }
  }, [templateId, allTemplates, dispatch]);

  function handleViewDataReady(loadedViewData: any, loadedBoreholeIds: number[]) {
    console.log('[handleViewDataReady] called', { isViewMode, initialViewFetchDone: initialViewFetchDoneRef.current, clientId, projectId, boreholeIds: loadedBoreholeIds });
    if (!isViewMode) { console.log('[handleViewDataReady] SKIP: not view mode'); return; }
    if (initialViewFetchDoneRef.current) { console.log('[handleViewDataReady] SKIP: initialViewFetchDone already true'); return; }
    if (!clientId || !projectId) { console.log('[handleViewDataReady] SKIP: missing clientId/projectId'); return; }

    let initialStartDepth = loadedViewData?.minimumDepth ?? null;
    let initialEndDepth = loadedViewData?.maximumDepth ?? null;

    if (initialStartDepth == null || initialEndDepth == null) {
      const minFromGroups = Math.min(
        ...(loadedViewData?.groups || [])
          .map((g: any) => Number(g?.minimumDepth))
          .filter((v: number) => !isNaN(v))
      );
      const maxFromGroups = Math.max(
        ...(loadedViewData?.groups || [])
          .map((g: any) => Number(g?.maximumDepth))
          .filter((v: number) => !isNaN(v))
      );
      if (!isNaN(minFromGroups) && !isNaN(maxFromGroups)) {
        initialStartDepth = minFromGroups;
        initialEndDepth = maxFromGroups;
      }
    }

    console.log('[handleViewDataReady] depths', { initialStartDepth, initialEndDepth });
    if (initialStartDepth == null || initialEndDepth == null || initialEndDepth - initialStartDepth <= 0) { console.log('[handleViewDataReady] SKIP: invalid depth range'); return; }

    console.log('[handleViewDataReady] viewTrackTypeIdsByBorehole', viewTrackTypeIdsByBorehole, 'trackTypeIds', trackTypeIds, 'imageTypeIds', imageTypeIds);
    const boreholesPayload = loadedBoreholeIds.map((bId: number) => {
      // In view mode the track types come from the view definition itself, not from
      // the single-borehole track hierarchy. Do NOT filter by combinedNoDataSet here:
      // that set is derived from the primary URL borehole's hierarchy and would
      // incorrectly exclude IDs that are valid for other boreholes in the view.
      // The page-load case works because the hierarchy query hasn't resolved yet
      // (set is empty), but view-switch fails once it has loaded.
      const allTrackIds = viewTrackTypeIdsByBorehole[bId] || trackTypeIds;
      console.log(`[handleViewDataReady] borehole ${bId}: allTrackIds=${JSON.stringify(allTrackIds)}`);
      if (allTrackIds.length === 0 && imageTypeIds.length === 0) return null;
      return {
        boreholeId: bId,
        projectId,
        trackTypeIds: allTrackIds,
        imageTypeIds
      };
    }).filter(Boolean) as Array<{ boreholeId: number; projectId: string; trackTypeIds: number[]; imageTypeIds: number[] }>;

    console.log('[handleViewDataReady] boreholesPayload', boreholesPayload);
    if (boreholesPayload.length === 0) { console.log('[handleViewDataReady] SKIP: empty boreholesPayload'); return; }

    initialViewFetchDoneRef.current = true;
    skipNextUnifiedFetchRef.current = true;

    setLastMultiTrackRequest({
      boreholeEntries: boreholesPayload,
      bufferedStart: initialStartDepth,
      bufferedEnd: initialEndDepth,
      pixelHeight: viewportHeight
    });
    setLastMultiTrackResponse(null);

    console.log('[handleViewDataReady] calling fetchViewScalesAndDataDirect', { startDepth: initialStartDepth, endDepth: initialEndDepth });
    fetchViewScalesAndDataDirect({
      boreholes: boreholesPayload,
      startDepth: initialStartDepth,
      endDepth: initialEndDepth
    });
  }

  const { isViewMode, viewBoreholeIds, onViewSelectChange } = useViewLogViewer({
    viewIdParam: viewId,
    clientId,
    projectId,
    boreholeId,
    activeTemplateId,
    viewData,
    viewLoading,
    selectedViewId,
    dispatch,
    history,
    setSelectedViewId,
    setSelectedBoreholeIds,
    setAddedBoreholeIds,
    setPendingBoreholeIds,
    setHasConfirmedBoreholes,
    onViewDataReady: handleViewDataReady
  });

  // Determine if we're in multi-borehole mode (either saved view or multi-borehole template)
  const isMultiBoreholeMode = isViewMode || selectedBoreholeIds.length > 1;

  // Right side panel handlers
  const onShowHideAdjustmentsPanel = useCallback(() => {
    if (showRightSidePanel && showAdjustmentsPanel) {
      // Same button clicked - toggle hide
      setShowRightSidePanel(false);
      setShowAddTrackPanel(false);
      setShowReorderPanel(false);
      setShowAdjustmentsPanel(false);
      setShowAddStatisticsPanel(false);
      setShowCorrelationPanel(false);
    } else {
      // Different button or panel not showing - show this section
      setShowRightSidePanel(true);
      setShowAddTrackPanel(false);
      setShowReorderPanel(false);
      setShowAdjustmentsPanel(true);
      setShowAddStatisticsPanel(false);
      setShowCorrelationPanel(false);
    }
  }, [showRightSidePanel, showAdjustmentsPanel]);

  const onShowAddTrackPanel = useCallback(() => {
    if (showRightSidePanel && showAddTrackPanel) {
      // Same button clicked - toggle hide
      setShowRightSidePanel(false);
      setShowAddTrackPanel(false);
      setShowReorderPanel(false);
      setShowAdjustmentsPanel(false);
      setShowAddStatisticsPanel(false);
      setShowCorrelationPanel(false);
    } else {
      // Different button or panel not showing - show this section
      setShowRightSidePanel(true);
      setShowAddTrackPanel(true);
      setShowReorderPanel(false);
      setShowAdjustmentsPanel(false);
      setShowAddStatisticsPanel(false);
      setShowCorrelationPanel(false);
    }
  }, [showRightSidePanel, showAddTrackPanel]);

  const onShowReorderPanel = useCallback(() => {
    if (showRightSidePanel && showReorderPanel) {
      // Same button clicked - toggle hide
      setShowRightSidePanel(false);
      setShowAddTrackPanel(false);
      setShowReorderPanel(false);
      setShowAdjustmentsPanel(false);
      setShowAddStatisticsPanel(false);
      setShowCorrelationPanel(false);
    } else {
      // Different button or panel not showing - show this section
      setShowRightSidePanel(true);
      setShowAddTrackPanel(false);
      setShowReorderPanel(true);
      setShowAdjustmentsPanel(false);
      setShowAddStatisticsPanel(false);
      setShowCorrelationPanel(false);
    }
  }, [showRightSidePanel, showReorderPanel]);

  const onShowAdjustments = useCallback(() => {
    if (showRightSidePanel && showAdjustmentsPanel) {
      // Same button clicked - toggle hide
      setShowRightSidePanel(false);
      setShowAddTrackPanel(false);
      setShowReorderPanel(false);
      setShowAdjustmentsPanel(false);
      setShowAddStatisticsPanel(false);
      setShowCorrelationPanel(false);
    } else {
      // Different button or panel not showing - show this section
      setShowRightSidePanel(true);
      setShowAddTrackPanel(false);
      setShowReorderPanel(false);
      setShowAdjustmentsPanel(true);
      setShowAddStatisticsPanel(false);
      setShowCorrelationPanel(false);
    }
  }, [showRightSidePanel, showAdjustmentsPanel]);

  const onShowCorrelationPanel = useCallback(() => {
    if (showRightSidePanel && showCorrelationPanel) {
      // Same button clicked - toggle hide
      setShowRightSidePanel(false);
      setShowAddTrackPanel(false);
      setShowReorderPanel(false);
      setShowAdjustmentsPanel(false);
      setShowAddStatisticsPanel(false);
      setShowCorrelationPanel(false);
    } else {
      // Different button or panel not showing - show this section
      setShowRightSidePanel(true);
      setShowAddTrackPanel(false);
      setShowReorderPanel(false);
      setShowAdjustmentsPanel(false);
      setShowAddStatisticsPanel(false);
      setShowCorrelationPanel(true);
    }
  }, [showRightSidePanel, showCorrelationPanel]);

  const onShowAddStatistics = useCallback(() => {
    if (showRightSidePanel && showAddStatisticsPanel) {
      // Same button clicked - toggle hide
      setShowRightSidePanel(false);
      setShowAddTrackPanel(false);
      setShowReorderPanel(false);
      setShowAdjustmentsPanel(false);
      setShowAddStatisticsPanel(false);
      setShowCorrelationPanel(false);
    } else {
      // Different button or panel not showing - show this section
      setShowRightSidePanel(true);
      setShowAddTrackPanel(false);
      setShowReorderPanel(false);
      setShowAdjustmentsPanel(false);
      setShowAddStatisticsPanel(true);
      setShowCorrelationPanel(false);
    }
  }, [showRightSidePanel, showAddStatisticsPanel]);

  const onAddStatisticalTrack = useCallback((trackTypes: any[]) => {
    dispatch(addStatisticsTracks({ trackTypes }));
  }, [dispatch]);

  const onDeleteStatisticalTrack = useCallback((trackType: any) => {
    confirmAlert({
      title: 'Confirm Delete Statistic Track',
      message: `Are you sure you want to delete ${trackType.displayName}?`,
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            dispatch(deleteStatisticsTrack({ trackTypeId: trackType.trackTypeId }));
          }
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  }, [dispatch]);

  const onHideAddTrackPanel = useCallback(() => {
    setShowRightSidePanel(false);
    setShowAddTrackPanel(false);
  }, []);

  const onSetSelectedTrack = useCallback((track: any, groupId?: any) => {
    setSelectedTrack(track ?? null);
    if (typeof groupId !== 'undefined') {
      setSelectedGroupId(groupId ?? null);
    }
  }, []);

  const onTrackHeaderClick = useCallback((track: any, groupId?: any) => {
    // Select the track
    setSelectedTrack(track ?? null);
    if (typeof groupId !== 'undefined') {
      setSelectedGroupId(groupId ?? null);
    }
    // Open the adjustments panel
    setShowRightSidePanel(true);
    setShowAddTrackPanel(false);
    setShowReorderPanel(false);
    setShowAdjustmentsPanel(true);
    setShowAddStatisticsPanel(false);
    setShowCorrelationPanel(false);
  }, []);

  const onSetTrackCollapsed = useCallback((trackId: string, collapsed: boolean, groupId?: any) => {
    if (isViewMode && groupId != null) {
      dispatch(setGroupTrackCollapsed({ groupId, trackId, collapsed }));
      return;
    }
    dispatch(setTrackCollapsed({ trackId, collapsed }));
  }, [dispatch, isViewMode]);

  const onSelectLithology = useCallback((_track: any, _lithologyLexiconId: string) => {
    // No-op for now; selection state not used in V2
  }, []);

  const onAddLithology = useCallback((trackTypeId: any, depth: number, boreholeIdArg?: number) => {
    const parsedDepth = Number(depth);
    if (!Number.isFinite(parsedDepth)) {
      return;
    }

    const boreholeIdNum = typeof boreholeIdArg === 'number' ? boreholeIdArg : (boreholeId ? Number(boreholeId) : undefined);

    const sourceTrack = isViewMode
      ? groupTrackData.find((t: any) => Number(t?.trackTypeId) === Number(trackTypeId) && Number(t?.boreholeId) === Number(boreholeIdNum))
      : trackData.find((t: any) => Number(t?.trackTypeId) === Number(trackTypeId));
    const existsAtDepth = (sourceTrack?.data || []).some((d: any) => Number(d?.depth) === parsedDepth);
    if (existsAtDepth) {
      return;
    }

    dispatch(addDataValue({
      trackTypeId,
      depth: parsedDepth,
      value: undefined,
      color: undefined,
      lithologyLexiconId: undefined,
      showView: isViewMode,
      boreholeId: boreholeIdNum
    }));
  }, [dispatch, isViewMode, boreholeId, groupTrackData, trackData]);

  const hasCurveChanges = useCallback((existingCurves: any[] | undefined, nextCurves: any[]) => {
    if (!Array.isArray(existingCurves)) return true;
    if (existingCurves.length !== nextCurves.length) return true;

    const curveCompareKeys = [
      'displayOrder',
      'trackTypeId',
      'lineColor',
      'fillColor',
      'pointColor',
      'lineStyle',
      'curveType',
      'fillStyle',
      'pointStyle',
      'lineSize',
      'fillSize',
      'pointSize',
      'showLine',
      'showFill',
      'showPoints',
      'xScaleMinimum',
      'xScaleMaximum',
      'manualScaleXMinimum',
      'manualScaleXMaximum',
      'majorIntervals',
      'minorIntervals',
      'manualMajorIntervals',
      'manualMinorIntervals',
      'logLessThaZero',
      'colorBasedOnLithology',
      'lithologyTrackTypeId'
    ];

    const existingById = new Map(existingCurves.map((c: any) => [String(c?.id), c]));
    for (const curve of nextCurves) {
      const existing = existingById.get(String(curve?.id));
      if (!existing) return true;
      for (const key of curveCompareKeys) {
        if (existing?.[key] != curve?.[key]) return true;
      }
    }

    return false;
  }, []);

  const onUpdateCurves = useCallback((templateTrackId: string, curves: any, groupId?: any) => {
    if (!Array.isArray(curves)) return;
    if (isViewMode) {
      if (groupId != null) {
        const group = viewData?.groups?.find((g: any) => g.groupId == groupId);
        const viewTrack = group?.tracks?.find((t: any) => t.id == templateTrackId || t.trackId == templateTrackId);
        if (!hasCurveChanges(viewTrack?.curves, curves)) return;

        // Detect genuinely new curves (by id) before dispatching
        const existingViewCurveIds = new Set((viewTrack?.curves || []).map((c: any) => String(c?.id)));
        const newViewCurveTrackTypeIds = curves
          .filter((c: any) => !existingViewCurveIds.has(String(c?.id)))
          .map((c: any) => Number(c.trackTypeId))
          .filter((id: number) => !isNaN(id) && id !== 0);

        dispatch(updateViewCurves({ groupId, trackId: templateTrackId, curves }));

        // Fetch data/scale for newly added curves
        if (newViewCurveTrackTypeIds.length > 0 && clientId && projectId && startDepth != null && endDepth != null) {
          const targetBoreholeId = Number(group?.boreholeId);
          if (!isNaN(targetBoreholeId)) {
            fetchViewScalesAndTrackDataDirect({
              clientId,
              boreholes: [{ boreholeId: targetBoreholeId, projectId, trackTypeIds: newViewCurveTrackTypeIds, imageTypeIds: [] }],
              startDepth,
              endDepth,
              pixelHeight: viewportHeight,
              depthUnit: template?.depthUnit ?? null,
              cumulativeGroups
            }).then((result) => {
              dispatch(mergeDirectTrackData({
                groupTrackData: result.groupTrackData,
                scaleMetadataByBorehole: result.scaleMetadataByBorehole,
                groupTrackImages: result.groupTrackImages
              }));
            });
          }
        }
      }
      return;
    }
    // Non-view mode: allow dispatch even for local (unsaved) templates — the reducer does not
    // use selectedTemplateId, so it is safe to pass null when no template ID exists yet.
    const templateTrack = template?.tracks?.find((t: any) => t.id == templateTrackId);
    if (!hasCurveChanges(templateTrack?.curves, curves)) return;

    // Detect genuinely new curves (by id) before dispatching
    const existingCurveIds = new Set((templateTrack?.curves || []).map((c: any) => String(c?.id)));
    const newCurveTrackTypeIds = curves
      .filter((c: any) => !existingCurveIds.has(String(c?.id)))
      .map((c: any) => Number(c.trackTypeId))
      .filter((id: number) => !isNaN(id) && id !== 0);

    const resolvedTemplateId = selectedTemplateId ?? activeTemplateId;
    dispatch(updateCurves({ selectedTemplateId: resolvedTemplateId, templateTrackId, curves }));

    // Fetch data/scale for newly added curves
    if (newCurveTrackTypeIds.length > 0 && clientId && projectId && startDepth != null && endDepth != null && boreholeId) {
      const boreholeIdNum = Number(boreholeId);
      if (!isNaN(boreholeIdNum)) {
        fetchViewScalesAndTrackDataDirect({
          clientId,
          boreholes: [{ boreholeId: boreholeIdNum, projectId, trackTypeIds: newCurveTrackTypeIds, imageTypeIds: [] }],
          startDepth,
          endDepth,
          pixelHeight: viewportHeight,
          depthUnit: template?.depthUnit ?? null,
          cumulativeGroups
        }).then((result) => {
          dispatch(mergeDirectTrackData({
            groupTrackData: result.groupTrackData,
            scaleMetadataByBorehole: result.scaleMetadataByBorehole,
            groupTrackImages: result.groupTrackImages
          }));
        });
      }
    }
  }, [dispatch, isViewMode, selectedTemplateId, activeTemplateId, template?.tracks, template?.depthUnit,
      viewData?.groups, hasCurveChanges, clientId, projectId, startDepth, endDepth, boreholeId, viewportHeight]);

  const updateCurve = useCallback((templateTrackId: string, curve: any) => {
    // For view mode with a groupId, update the full curves array to avoid deleting other curves
    if (isViewMode) {
      const groupId = selectedGroupId;
      if (groupId != null) {
        const group = viewData?.groups?.find((g: any) => g.groupId == groupId);
        const viewTrack = group?.tracks?.find((t: any) => t.id == templateTrackId || t.trackId == templateTrackId);
        if (viewTrack?.curves) {
          const updatedCurves = viewTrack.curves.map((c: any) =>
            String(c?.id) === String(curve?.id) ? { ...c, ...curve } : c
          );
          dispatch(updateViewCurves({ groupId, trackId: templateTrackId, curves: updatedCurves }));
        }
      }
      return;
    }
    // Non-view mode: use the single-curve action which only updates scale fields, never deletes curves
    dispatch(updateCurveAction({ templateTrackId, curve }));
  }, [dispatch, isViewMode, viewData?.groups, selectedGroupId]);

  const onSetShowGridlines = useCallback((templateTrackId: string, show: boolean) => {
    const resolvedTemplateId = selectedTemplateId ?? activeTemplateId;
    if (!resolvedTemplateId) return;
    dispatch(setShowGridlines({ selectedTemplateId: resolvedTemplateId, templateTrackId, show }));
  }, [dispatch, selectedTemplateId, activeTemplateId]);

  const onSetScaleType = useCallback((templateTrackId: string, scaleType: number) => {
    const resolvedTemplateId = selectedTemplateId ?? activeTemplateId;
    if (!resolvedTemplateId) return;
    dispatch(setScaleType({ selectedTemplateId: resolvedTemplateId, templateTrackId, scaleType }));
  }, [dispatch, selectedTemplateId, activeTemplateId]);

  const onAddCurve = useCallback((trackId: any) => {
    dispatch(addCurve({ trackId }));
  }, [dispatch]);

  const onRemoveTrack = useCallback((trackTypeId: any, trackId?: any, groupId?: any) => {
    if (isViewMode && groupId != null) {
      const resolvedTrackId = typeof trackId !== 'undefined' ? trackId : trackTypeId;
      dispatch(deleteTrack({ trackId: resolvedTrackId, groupId }));
    }
    dispatch(removeTrack({ trackTypeId }));
  }, [dispatch, isViewMode]);

  const onCollapseGroup = useCallback((groupId: any) => {
    dispatch(setGroupCollapsed({ groupId }));
  }, [dispatch]);

  const proceedWithTemplateSwitch = useCallback((nextId: any, level: number) => {
    if (selectedViewId) {
      setSelectedViewId(null);
      setSelectedBoreholeIds([]);
      setAddedBoreholeIds([]);
      setPendingBoreholeIds([]);
      setHasConfirmedBoreholes(false);
    }
    dispatch(clearTrackData());
    dispatch(clearTrackDataCache());
    dispatch(clearScaleMetadata());
    // Clear the loaded template so that template.tracks becomes empty immediately.
    // This prevents the unified fetch from firing with the OLD template's trackTypeIds
    // before the new getTemplate call resolves — which would stamp lastUnifiedFetchKey
    // with those old IDs and cause the subsequent fetch (with identical IDs on the new
    // template) to be deduplicated away, leaving the charts permanently blank.
    dispatch(clearTemplate());
    // Reset the fetch-dedup keys so the unified effect always runs a fresh fetch
    // for the new template, even when track type IDs and depth range are identical
    // to those of the previous template.
    lastUnifiedFetchRef.current = '';
    lastUnifiedFetchKey = '';
    isUnifiedFetchInFlight = false;
    skipNextUnifiedFetchRef.current = false;
    setNoDataTrackTypeIds([]);
    setAddedTrackTypeIds([]);
    setIsLocalTemplate(false);
    setActiveTemplateId(String(nextId));
    setAddTrackTargetBoreholeId('all');

    // Reset the visible depth range to the full borehole extent whenever the user
    // switches templates so that the subsequent data fetch covers the whole borehole.
    const fullRange = boreholeFullDepthRef.current;
    if (fullRange != null) {
      setStartDepth(fullRange.minDepth);
      setEndDepth(fullRange.maxDepth);
    }
    setManualDepthRange(false);
    lastScaleFetchedRangeRef.current = null;

    switch (level) {
      case 0:
        dispatch(setSelectedBuiltInTemplateId(nextId));
        break;
      case 1:
        dispatch(setSelectedUserTemplateId(nextId));
        break;
      case 2:
        dispatch(setSelectedBoreholeTemplateId(nextId));
        break;
      case 3:
        dispatch(setSelectedProjectTemplateId(nextId));
        break;
      case 4:
        dispatch(setSelectedClientTemplateId(nextId));
        break;
      default:
        dispatch(setSelectedTemplateId(nextId));
        break;
    }

    history.push(`/client/${clientId}/project/${projectId}/borehole/${boreholeId}/log-viewer/${nextId}`);
  }, [
    clientId,
    projectId,
    boreholeId,
    selectedViewId,
    dispatch,
    history,
  ]);

  const handleTemplateSelection = useCallback((nextId: any, level: number) => {
    if (!nextId || !clientId || !projectId || !boreholeId) return;
    if (activeTemplateId && String(nextId) === activeTemplateId) return;
    if (templateNotSaved || dataNotSaved?.some?.((x: any) => x)) {
      dispatch(setNextTemplateId({ nextTemplateId: nextId, nextTemplateLevel: level }));
      setShowNotSavedModal(true);
      return;
    }
    proceedWithTemplateSwitch(nextId, level);
  }, [
    clientId,
    projectId,
    boreholeId,
    activeTemplateId,
    templateNotSaved,
    dataNotSaved,
    dispatch,
    proceedWithTemplateSwitch,
  ]);

  useEffect(() => {
    if (urlTemplateId) {
      const url = `/client/${clientId}/project/${projectId}/borehole/${boreholeId}/log-viewer/${urlTemplateId}`;
      dispatch(clearUrlTemplateId());
      history.push(url);
    }
  }, [urlTemplateId]);

  const onChangeBuiltInTemplate = useCallback((e: any) => {
    handleTemplateSelection(e?.value, 0);
  }, [handleTemplateSelection]);

  const onChangeUserTemplate = useCallback((e: any) => {
    handleTemplateSelection(e?.value, 1);
  }, [handleTemplateSelection]);

  const onChangeBoreholeTemplate = useCallback((e: any) => {
    handleTemplateSelection(e?.value, 2);
  }, [handleTemplateSelection]);

  const onChangeProjectTemplate = useCallback((e: any) => {
    handleTemplateSelection(e?.value, 3);
  }, [handleTemplateSelection]);

  const onChangeClientTemplate = useCallback((e: any) => {
    handleTemplateSelection(e?.value, 4);
  }, [handleTemplateSelection]);

  const handleViewSelection = useCallback((nextId: any, level: number) => {
    if (!nextId || !clientId || !projectId || !boreholeId) return;
    dispatch(clearTrackData());
    dispatch(clearTrackDataCache());
    dispatch(clearScaleMetadata());
    lastUnifiedFetchRef.current = '';
    lastUnifiedFetchKey = '';
    isUnifiedFetchInFlight = false;
    skipNextUnifiedFetchRef.current = false;
    initialViewFetchDoneRef.current = false;
    setAddedBoreholeIds([]);
    setActiveTemplateId(null);
    setIsLocalTemplate(false);
    setSelectedViewId(String(nextId));

    if (level === 1) {
      dispatch(setSelectedUserViewId(nextId));
    } else {
      dispatch(setSelectedClientViewId(nextId));
    }

    history.push(`/client/${clientId}/project/${projectId}/borehole/${boreholeId}/log-viewer/view/${nextId}`);
  }, [
    clientId,
    projectId,
    boreholeId,
    dispatch,
    history,
    setActiveTemplateId,
    setIsLocalTemplate,
    setSelectedViewId
  ]);

  const onChangeUserView = useCallback((e: any) => {
    handleViewSelection(e?.value, 1);
  }, [handleViewSelection]);

  const onChangeClientView = useCallback((e: any) => {
    handleViewSelection(e?.value, 4);
  }, [handleViewSelection]);

  const selectedTemplate = useMemo(() => {
    const id = selectedTemplateId ?? activeTemplateId;
    if (!id) return null;
    return (allTemplates || []).find((t: any) => String(t.id) === String(id)) ?? null;
  }, [allTemplates, selectedTemplateId, activeTemplateId]);

  const currentTemplateLevel = useMemo(() => {
    const levelValue = selectedTemplate?.templateLevel;
    const level = templateLevels.find((l: any) => l.value === levelValue);
    return level?.name ?? null;
  }, [selectedTemplate, templateLevels]);

  const currentViewLevel = useMemo(() => {
    const levelValue = viewData?.templateLevel ?? viewData?.viewLevel;
    const level = templateLevels.find((l: any) => l.value === levelValue);
    return level?.name ?? null;
  }, [viewData, templateLevels]);

  const onSaveTemplate = useCallback((templateName?: string) => {    
    const resolvedId = selectedTemplateId ?? activeTemplateId;
    if (!resolvedId || !template) return;
    const templateLevel = selectedTemplate?.templateLevel ?? template?.templateLevel ?? 1;
    dispatch(updateTemplate({
      ...template,
      id: resolvedId,
      name: templateName || saveTemplateName,
      templateLevel
    }));
  }, [dispatch, selectedTemplateId, activeTemplateId, template, selectedTemplate, saveTemplateName]);

  const handleCloseNotSavedModal = useCallback(() => {
    setShowNotSavedModal(false);
  }, []);

  const handleYesNotSavedModal = useCallback(() => {
    setShowNotSavedModal(false);
    if (templateNotSaved && canSaveTemplate) {
      onSaveTemplate();
    }
    if (nextTemplateId != null) {
      proceedWithTemplateSwitch(nextTemplateId, nextTemplateLevel ?? 0);
      dispatch(setNextTemplateId(null));
    }
  }, [templateNotSaved, canSaveTemplate, nextTemplateId, nextTemplateLevel, onSaveTemplate, proceedWithTemplateSwitch, dispatch]);

  const handleNoNotSavedModal = useCallback(() => {
    setShowNotSavedModal(false);
    dispatch(clearNotSaved());
    if (nextTemplateId != null) {
      proceedWithTemplateSwitch(nextTemplateId, nextTemplateLevel ?? 0);
      dispatch(setNextTemplateId(null));
    }
  }, [nextTemplateId, nextTemplateLevel, proceedWithTemplateSwitch, dispatch]);

  const onSaveView = useCallback(() => {
    debugger;
    if (!selectedViewId || !viewData) return;
    dispatch(updateView({
      ...viewData,
      viewId: selectedViewId,
      name: saveViewName
    }));
  }, [dispatch, selectedViewId, viewData, saveViewName]);

  const onSaveTemplateAs = useCallback(() => {
    const baseName = template?.name ? `${template.name} Copy` : 'Template Copy';
    let copyName = baseName;
    let copyCount = 1;
    while ((allTemplates || []).find((t: any) => t.name?.toLowerCase() === copyName.toLowerCase().trim())) {
      copyName = `${baseName} ${copyCount}`;
      copyCount += 1;
    }
    setSaveTemplateName(copyName);
    setSaveTemplateNameError(null);
    setShowSaveAsModal(true);
  }, [template, allTemplates]);

  const onSaveViewAs = useCallback(() => {
    const baseName = viewData?.name ? `${viewData.name} Copy` : 'View Copy';
    let copyName = baseName;
    let copyCount = 1;
    while ((allViews || []).find((t: any) => t.name?.toLowerCase() === copyName.toLowerCase().trim())) {
      copyName = `${baseName} ${copyCount}`;
      copyCount += 1;
    }
    setSaveViewName(copyName);
    setSaveViewNameError(null);
    setShowSaveViewAsModal(true);
  }, [viewData, allViews]);

  const handleOkSaveAsModal = useCallback(() => {
    if ((allTemplates || []).find((t: any) => t.templateLevel == saveAsTemplateLevel && t.name?.toLowerCase() === saveTemplateName.toLowerCase().trim())) {
      setSaveTemplateNameError(['Cannot have duplicate names in the same level.']);
      return;
    }
    if (!template || !clientId || !projectId || !boreholeId) return;
    dispatch(addTemplate({
      ...template,
      name: saveTemplateName,
      changeToNew: true,
      templateType: 0,
      templateLevel: +saveAsTemplateLevel,
      clientId,
      projectId,
      boreholeId
    }));
    setShowSaveAsModal(false);
  }, [
    allTemplates,
    saveAsTemplateLevel,
    saveTemplateName,
    template,
    clientId,
    projectId,
    boreholeId,
    dispatch
  ]);

  const handleOkSaveViewAsModal = useCallback(async () => {
    if ((allViews || []).find((t: any) => t.viewLevel == saveAsViewLevel && t.name?.toLowerCase() === saveViewName.toLowerCase().trim())) {
      setSaveViewNameError(['Cannot have duplicate names in the same level.']);
      return;
    }
    if (!viewData || !clientId || !projectId || !boreholeId) return;
    const result = await dispatch(addView({
      ...viewData,
      name: saveViewName,
      changeToNew: true,
      templateType: 0,
      templateLevel: +saveAsViewLevel,
      clientId,
      projectId,
      boreholeId
    })).unwrap();
    setShowSaveViewAsModal(false);
    if (result?.viewId) {
      history.push(`/client/${clientId}/project/${projectId}/borehole/${boreholeId}/log-viewer/view/${result.viewId}`);
    }
  }, [
    allViews,
    saveAsViewLevel,
    saveViewName,
    viewData,
    clientId,
    projectId,
    boreholeId,
    dispatch,
    history
  ]);

  const handleCloseSaveAsModal = useCallback(() => {
    setShowSaveAsModal(false);
  }, []);

  const handleCloseSaveViewAsModal = useCallback(() => {
    setShowSaveViewAsModal(false);
  }, []);

  const onShowSaveTemplateDialog = useCallback(() => {
    setShowSaveAsModal(true);
  }, []);

  const onShowSaveViewDialog = useCallback(() => {
    setShowSaveViewAsModal(true);
  }, []);

  const onDeleteTemplate = useCallback(() => {
    const resolvedId = selectedTemplateId ?? activeTemplateId;
    if (!resolvedId) return;
    confirmAlert({
      title: 'Confirm Delete Template',
      message: `Are you sure you want to delete template ${template?.name}?`,
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            dispatch(deleteTemplate({ templateId: resolvedId, templateLevel: saveAsTemplateLevel }));
          }
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  }, [dispatch, selectedTemplateId, activeTemplateId, template?.name, saveAsTemplateLevel]);

  const onNewTemplate = useCallback(() => {
    dispatch(addNewTemplate());
  }, [dispatch]);

  const onNewView = useCallback(() => {
    onSaveViewAs();
  }, [onSaveViewAs]);

  const moveTemplateLevel = useCallback((level: number) => {
    const resolvedId = selectedTemplateId ?? activeTemplateId;
    if (!resolvedId) return;
    setMoveTemplateError(null);
    dispatch(changeTemplateLevel({ id: resolvedId, templateLevel: level, projectId, boreholeId }))
      .unwrap?.()
      .catch((error: any) => {        
        setMoveTemplateError({errors: error})});
  }, [dispatch, selectedTemplateId, activeTemplateId, clientId, projectId, boreholeId]);

  const moveViewLevel = useCallback((level: number) => {
    const resolvedId = selectedViewId;
    if (!resolvedId) return;
    dispatch(changeViewLevel({ id: resolvedId, viewLevel: level, clientId, projectId, boreholeId }))
      .unwrap?.()
      .catch((error: any) => setMoveViewError(error));
  }, [dispatch, selectedViewId, clientId, projectId, boreholeId ]);

  const { data: boreholeData } = useGetBoreholeSummaryQuery(
    { boreholeId, clientId, projectId },
    { skip: !clientId || !projectId || !boreholeId || !!viewId, refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (boreholeData?.name) {
      setBorehole({name: boreholeData?.name, id: boreholeId});        
    }
  },[boreholeData?.name])

  const depthUnit = useMemo(() => {
    return template?.depthUnit ?? boreholeData?.depthTypes?.[depthType]?.depthUnit ?? null;
  }, [template?.depthUnit, boreholeData, depthType]);

  // Initialize left panel hooks (placed here because it requires depthUnit)
  const panelLogic = useBoreholeLogPanels({
    setStartDepth,
    setEndDepth,
    startDepth,
    endDepth,
    depthUnit,
    setDepthType,
    clientId,
    projectId,
    boreholeId,
    selectedTemplateId: selectedTemplateId ?? activeTemplateId,
    selectedViewId,
    depthType,
    isViewMode,
    isMultiBoreholeMode,
    connection,
    connected,
    trackTypesHierarchyData
  });

  // When the depth unit changes (m ↔ ft) the existing cached track data is in the
  // old unit and will render incorrectly until new data arrives.  Clear it eagerly
  // so charts show an empty/loading state rather than mismatched geometry.
  const prevDepthUnitRef = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    if (prevDepthUnitRef.current === undefined) {
      // Skip on initial mount – data hasn't been fetched yet.
      prevDepthUnitRef.current = depthUnit;
      return;
    }
    if (prevDepthUnitRef.current !== depthUnit) {
      prevDepthUnitRef.current = depthUnit;
      dispatch(clearTrackData());
      dispatch(clearTrackDataCache());
      dispatch(clearScaleMetadata());
    }
  }, [depthUnit, dispatch]);

  const viewMinDepth = viewData?.minimumDepth ?? null;
  const viewMaxDepth = viewData?.maximumDepth ?? null;
  const minDepth = isViewMode && viewMinDepth != null
    ? viewMinDepth
    : (boreholeData?.depthTypes?.[depthType]?.minDepth ?? 0);
  // Extend maxDepth to cover any unsaved rows (e.g. new stratigraphy) whose toDepth
  // exceeds the borehole's recorded maximum — templateMaxDepth is updated immediately
  // by addStratigraphyValue before any backend round-trip.
  const maxDepth = isViewMode && viewMaxDepth != null
    ? viewMaxDepth
    : Math.max(
        boreholeData?.depthTypes?.[depthType]?.maxDepth ?? 0,
        templateMaxDepth ?? 0
      );
  // Keep the ref in sync so template-change callbacks can read the current extents.
  boreholeFullDepthRef.current = !isViewMode && boreholeData?.depthTypes?.[depthType]
    ? { minDepth: boreholeData.depthTypes[depthType].minDepth ?? 0, maxDepth: boreholeData.depthTypes[depthType].maxDepth ?? 0 }
    : null;
  const showValueAxisAnnotation = isViewMode
    ? viewData?.showValueAxisAnnotation
    : template?.showValueAxisAnnotation;
  const hasLoggedDepth = Boolean(boreholeData?.depthTypes?.[depthType]);
  const showSummaryReport = Boolean(boreholeData?.showSummaryReport && !isViewMode);
  const [metaDataDepth, setMetaDataDepth] = useState<any>();

  useEffect(() => {
    if (!boreholeId) return;
    const idNum = Number(boreholeId);
    if (isNaN(idNum)) return;
    setSelectedBoreholeIds((prev) => (prev.length ? prev : [idNum]));
    setPendingBoreholeIds((prev) => (prev.length ? prev : [idNum]));
  }, [boreholeId]);

  const { onTemplateSelectChange } = useTemplateLogViewer({
    templateIdParam: templateId,
    activeTemplateId,
    isLocalTemplate,
    clientId,
    projectId,
    boreholeId,
    selectedViewId,
    dispatch,
    history,
    setActiveTemplateId,
    setIsLocalTemplate,
    setSelectedViewId,
    setSelectedBoreholeIds,
    setAddedBoreholeIds,
    setPendingBoreholeIds,
    setHasConfirmedBoreholes,
    setNoDataTrackTypeIds,
    setAddedTrackTypeIds,
    setLastTrackRequest,
    setAddTrackTargetBoreholeId
  });

  useEffect(() => {
    if (activeTemplateId) return;
    if (isLocalTemplate) return;
    if (!templateId) return;
    if (!templatesLoaded || !builtInTemplates || builtInTemplates.length === 0) return;
    setActiveTemplateId(String(builtInTemplates[0].id));
  }, [templatesLoaded, builtInTemplates, activeTemplateId, isLocalTemplate, templateId]);

  useEffect(() => {
    if (!activeTemplateId) return;
    dispatch(getTemplate(activeTemplateId));
  }, [activeTemplateId, dispatch]);

  useEffect(() => {
    if (!activeTemplateId) return;
    if (lastTemplateIdRef.current === activeTemplateId) return;
    lastTemplateIdRef.current = activeTemplateId;

    setLastTrackRequest(null);
    dispatch(clearTrackData());
    dispatch(clearTrackDataCache());
    dispatch(clearScaleMetadata());
    // Reset dedup keys for any code path that changes activeTemplateId (e.g. the
    // useTemplateLogViewer URL-param sync) so the unified fetch always re-runs.
    lastUnifiedFetchRef.current = '';
    lastUnifiedFetchKey = '';
    isUnifiedFetchInFlight = false;
    skipNextUnifiedFetchRef.current = false;
  }, [activeTemplateId, dispatch]);

  const templateOptions = useMemo(() => {
    const source = allTemplates.length ? allTemplates : builtInTemplates;
    return source
      .filter((t: any) => t && t.id)
      .map((t: any) => ({
        id: String(t.id),
        name: t.name || `Template ${t.id}`,
        level: t.levelName || t.templateLevel || t.level || '',
        hasData: typeof t?.containsData === 'boolean' ? t.containsData : true,
      }))
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [allTemplates, builtInTemplates]);

  const builtInTemplateOptions = useMemo(() => {
    return (allTemplates || [])
      .filter((t: any) => t?.templateLevel === 0)
      .map((t: any) => ({ label: t.name, value: t.id, isDisabled: !t.containsData }));
  }, [allTemplates]);

  const userTemplateOptions = useMemo(() => {
    return (allTemplates || [])
      .filter((t: any) => t?.templateLevel === 1)
      .map((t: any) => ({ label: t.name, value: t.id, isDisabled: !t.containsData }));
  }, [allTemplates]);

  const boreholeTemplateOptions = useMemo(() => {
    return (allTemplates || [])
      .filter((t: any) => t?.templateLevel === 2)
      .map((t: any) => ({ label: t.name, value: t.id, isDisabled: !t.containsData }));
  }, [allTemplates]);

  const projectTemplateOptions = useMemo(() => {
    return (allTemplates || [])
      .filter((t: any) => t?.templateLevel === 3)
      .map((t: any) => ({ label: t.name, value: t.id, isDisabled: !t.containsData }));
  }, [allTemplates]);

  const clientTemplateOptions = useMemo(() => {
    return (allTemplates || [])
      .filter((t: any) => t?.templateLevel === 4)
      .map((t: any) => ({ label: t.name, value: t.id, isDisabled: !t.containsData }));
  }, [allTemplates]);

  const userViewOptions = useMemo(() => {
    return (allViews || [])
      .filter((v: any) => v?.templateLevel === 1)
      .map((v: any) => ({ label: v.name, value: v.id }));
  }, [allViews]);

  const clientViewOptions = useMemo(() => {
    return (allViews || [])
      .filter((v: any) => v?.templateLevel === 4)
      .map((v: any) => ({ label: v.name, value: v.id }));
  }, [allViews]);

  useEffect(() => {
    if (template?.name != null) {
      setSaveTemplateName(template.name);
    }
  }, [template?.name]);

  useEffect(() => {
    if (viewData?.name != null) {
      setSaveViewName(viewData.name);
    }
  }, [viewData?.name]);

  useEffect(() => {    
    if (!isViewMode) return;
    const currentViewId = String(viewId ?? selectedViewId ?? '');
    if (!currentViewId || currentViewId === lastViewIdRef.current) return;
    console.log('[viewId reset effect] new viewId detected', { currentViewId, prev: lastViewIdRef.current });
    lastViewIdRef.current = currentViewId;
    lastUnifiedFetchRef.current = '';
    lastUnifiedFetchKey = '';
    isUnifiedFetchInFlight = false;
    skipNextUnifiedFetchRef.current = false;
    initialViewFetchDoneRef.current = false;
    setAddedBoreholeIds([]);
    setDirectGroupTrackData([]);
    setDirectScaleMetadataByBorehole({});
    setDirectGroupTrackImages([]);
    setDirectScalesLoaded(false);
    setDirectLoadingHighResolution(false);
  }, [isViewMode, viewId, selectedViewId]);

  const boreholeOptions = useMemo(() => {
    if (!projectId) return [] as Array<{ id: number; name: string; minDepth?: number; maxDepth?: number }>;
    const project = boreholesByProject.find((p: any) => String(p.id) === String(projectId));
    const boreholes = project?.boreholes || [];
    return boreholes.map((b: any) => ({
      id: Number(b.id),
      name: b.name,
      minDepth: b.minBoreholeDepth,
      maxDepth: b.maxBoreholeDepth
    })).filter((b: any) => !isNaN(b.id));
  }, [boreholesByProject, projectId]);

  const viewOptions = useMemo(() => {
    return (allViews || [])
      .filter((v: any) => v && v.id)
      .map((v: any) => ({ id: String(v.id), name: v.name || `View ${v.id}` }))
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [allViews]);

  const viewTrackTypeIdsByBorehole = useMemo(() => {
    const map: { [boreholeId: number]: number[] } = {};
    if (!isViewMode || !viewData?.groups) return map;

    viewData.groups.forEach((group: any) => {
      const boreholeIdNum = Number(group?.boreholeId);
      if (isNaN(boreholeIdNum)) return;
      if (!map[boreholeIdNum]) {
        map[boreholeIdNum] = [];
      }
      group?.tracks?.forEach((track: any) => {
        if (track?.trackType === 1 && typeof track?.imageType === 'number') {
          // image tracks handled separately
        }

        if (Array.isArray(track?.curves)) {
          track.curves.forEach((curve: any) => {
            const ttId = Number(curve?.trackTypeId);
            if (!isNaN(ttId) && !map[boreholeIdNum].includes(ttId)) {
              map[boreholeIdNum].push(ttId);
            }
          });
        }

        const possibleIds = [
          Number(track?.trackTypeId),
          Number(track?.lithologyType),
          Number(track?.lithologyTypeId),
          Number(track?.stratigraphyTrackTypeId),
          Number(track?.textTrackTypeId)
        ];
        possibleIds.forEach((id) => {
          if (!isNaN(id) && id !== 0 && !map[boreholeIdNum].includes(id)) {
            map[boreholeIdNum].push(id);
          }
        });
      });
    });

    return map;
  }, [isViewMode, viewData]);

  const cumulativeGroups = useMemo(() => {
    const tracks = isViewMode
      ? (viewData?.groups || []).flatMap((group: any) => group?.tracks || [])
      : (template?.tracks || []);
    return buildCumulativeGroupsFromTracks(tracks);
  }, [isViewMode, viewData, template?.tracks]);

  const fetchViewScalesAndDataDirect = useCallback(async (params: {
    boreholes: Array<{ boreholeId: number; projectId: string; trackTypeIds: number[]; imageTypeIds: number[] }>;
    startDepth: number;
    endDepth: number;
    skipScaleCheck?: boolean;
  }) => {
    if (!clientId) return;
    const { boreholes, startDepth, endDepth, skipScaleCheck = false } = params;
    if (!boreholes || boreholes.length === 0) return;
    if (startDepth == null || endDepth == null || endDepth - startDepth <= 0) return;

    console.log('[fetchViewScalesAndDataDirect] starting fetch', { boreholes, startDepth, endDepth });
    setDirectLoadingHighResolution(true);
    setDirectScalesLoaded(false);

    const result = await fetchViewScalesAndTrackDataDirect({
      clientId,
      boreholes,
      startDepth,
      endDepth,
      pixelHeight: viewportHeight,
      depthUnit: template?.depthUnit ?? null,
      cumulativeGroups,
      existingScaleMetadataByBorehole: directScaleMetadataByBorehole,
      existingGroupTrackImages: directGroupTrackImages,
      existingGroupTrackData: directGroupTrackData,
      skipScaleCheck
    });

    console.log('[fetchViewScalesAndDataDirect] result', { scalesLoaded: result.scalesLoaded, groupTrackDataLength: result.groupTrackData?.length, scaleMetadataKeys: Object.keys(result.scaleMetadataByBorehole || {}) });
    setDirectScaleMetadataByBorehole(result.scaleMetadataByBorehole);
    setDirectGroupTrackImages(result.groupTrackImages);
    setDirectGroupTrackData(result.groupTrackData);
    setDirectScalesLoaded(result.scalesLoaded);
    setDirectLoadingHighResolution(false);
  }, [
    clientId,
    viewportHeight,
    template?.depthUnit,
    directScaleMetadataByBorehole,
    directGroupTrackImages,
    directGroupTrackData,
    cumulativeGroups
  ]);

  const commonViewTrackTypeIds = useMemo(() => {
    if (!isViewMode) return [] as number[];
    const boreholeIds = Object.keys(viewTrackTypeIdsByBorehole)
      .map((id) => Number(id))
      .filter((id) => !addedBoreholeIds.includes(id));
    if (boreholeIds.length === 0) return [] as number[];
    const sets = boreholeIds.map((id) => new Set(viewTrackTypeIdsByBorehole[id] || []));
    const [first, ...rest] = sets;
    const common: number[] = [];
    first.forEach((id) => {
      if (rest.every((set) => set.has(id))) {
        common.push(id);
      }
    });
    return common;
  }, [isViewMode, viewTrackTypeIdsByBorehole, addedBoreholeIds]);


  const trackTypeOptions = useMemo(() => {
    const categories = trackTypesHierarchyData?.trackTypeCategories || [];
    const allTypes = categories.reduce((acc: any[], cat: any) => {
      if (Array.isArray(cat?.trackTypes)) {
        acc.push(...cat.trackTypes);
      }
      return acc;
    }, [] as any[]);

    const options = allTypes
      .filter((t: any) => t && t.id)
      .map((t: any) => ({
        id: String(t.id),
        name: t.name || `Track ${t.id}`,
        dataStatus: typeof t?.hasData === 'boolean'
          ? (t.hasData ? 'has-data' : 'no-data')
          : typeof t?.noData === 'boolean'
            ? (t.noData ? 'no-data' : 'has-data')
            : typeof t?.dataCount === 'number'
              ? (t.dataCount > 0 ? 'has-data' : 'no-data')
              : typeof t?.points === 'number'
                ? (t.points > 0 ? 'has-data' : 'no-data')
                : 'unknown',
        units: t.units,
        allowedCurveTypes: Array.isArray(t.allowedCurveTypes) && t.allowedCurveTypes.length > 0
          ? t.allowedCurveTypes
          : ['line']
      }))
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
    return showOnlyTrackTypesWithData
      ? options.filter((t: any) => t.dataStatus === 'has-data')
      : options;
  }, [trackTypesHierarchyData, showOnlyTrackTypesWithData]);

  const trackTypeNameById = useMemo(() => {
    const map: { [id: number]: string } = {};
    trackTypeOptions.forEach((t: any) => {
      const idNum = Number(t.id);
      if (!isNaN(idNum)) {
        map[idNum] = t.name;
      }
    });

    if (isViewMode && viewData?.groups) {
      viewData.groups.forEach((group: any) => {
        group?.tracks?.forEach((track: any) => {
          const candidates = [
            { id: Number(track?.trackTypeId), name: track?.displayName || track?.trackName },
            { id: Number(track?.lithologyTypeId), name: track?.displayName || track?.trackName },
            { id: Number(track?.stratigraphyTrackTypeId), name: track?.displayName || track?.trackName },
            { id: Number(track?.textTrackTypeId), name: track?.displayName || track?.trackName }
          ];
          candidates.forEach((c) => {
            if (!isNaN(c.id) && c.name) {
              map[c.id] = c.name;
            }
          });

          if (Array.isArray(track?.curves)) {
            track.curves.forEach((curve: any) => {
              const idNum = Number(curve?.trackTypeId);
              const name = curve?.displayName || track?.displayName || track?.trackName;
              if (!isNaN(idNum) && name) {
                map[idNum] = name;
              }
            });
          }
        });
      });
    }
    return map;
  }, [trackTypeOptions, isViewMode, viewData]);

  const onAddTrack = () => {
    if (!boreholeId) return;

    // Logged depth hides lithology/text/stratigraphy tracks; block add from stale UI state too.
    if (isLoggedDepthTypeSelected && TRACK_TYPES_HIDDEN_IN_LOGGED_DEPTH.has(selectedTemplateTrackType)) {
      setSelectedTrackTypeIds([]);
      setCheckedLithologyTypes([]);
      setCheckedTextTypes([]);
      setCheckedStratigraphyTypes([]);
      return;
    }

    // Resolve effective IDs and track type name from the appropriate checked list
    const trackType = selectedTemplateTrackType;
    let effectiveIds: number[] = [];
    if (trackType === 1) {
      effectiveIds = checkedImageTypes.slice();
    } else if (trackType === 5) {
      effectiveIds = checkedLithologyTypes.slice();
    } else if (trackType === 7) {
      effectiveIds = checkedTextTypes.slice();
    } else if (trackType === 9) {
      effectiveIds = checkedStratigraphyTypes.slice();
    } else {
      // Graph / cumulative / tadpole / horizontal bar — use selectedTrackTypeIds
      effectiveIds = selectedTrackTypeIds.slice();
    }

    if (effectiveIds.length === 0) return;

    if (!isViewMode && !activeTemplateId && !isLocalTemplate) {
      dispatch(addNewTemplate());
      dispatch(clearTrackData());
      dispatch(clearTrackDataCache());
      dispatch(clearScaleMetadata());
      setNoDataTrackTypeIds([]);
      setAddedTrackTypeIds([]);
      setIsLocalTemplate(true);
    }

    setNoDataTrackTypeIds((prev) => prev.filter((id) => !effectiveIds.includes(id)));
    setAddedTrackTypeIds(effectiveIds);

    if (selectedBoreholeIds.length === 0 && boreholeId) {
      const idNum = Number(boreholeId);
      if (!isNaN(idNum)) {
        setSelectedBoreholeIds([idNum]);
      }
    }

    const targetBoreholeIds = isMultiBoreholeMode
      ? (addTrackTargetBoreholeId !== 'all'
          ? [Number(addTrackTargetBoreholeId)]
          : (selectedBoreholeIds.length > 0 ? selectedBoreholeIds : (boreholeId ? [Number(boreholeId)] : [])))
      : (boreholeId ? [Number(boreholeId)] : []);

    if (isMultiBoreholeMode && addTrackTargetBoreholeId !== 'all') {
      const targetId = Number(addTrackTargetBoreholeId);
      if (!isNaN(targetId)) {
        setAddedBoreholeIds([targetId]);
        setSelectedBoreholeIds((prev) => (prev.includes(targetId) ? prev : [...prev, targetId]));
      }
    }

    if (!isViewMode) {
      if (trackType === 1) {
        // Image tracks
        const imageTrackTypes = checkedImageTypes.map((id) => {
          const imageType = (imageTypes?.imageTypes || []).find((t: any) => t.value === id || Number(t.value) === id);
          return { imageType: id, displayName: imageType?.name || `Image ${id}`, legendName: imageType?.legendName, legendRollover: imageType?.legendRollover };
        });
        dispatch(addImageTracks({ imageTrackTypes, boreholeIds: targetBoreholeIds }));
      } else if (trackType === 5) {
        // Lithology tracks
        const categories = trackTypesHierarchyData?.trackTypeCategories || [];
        const lithologyCat = categories.find((c: any) => String(c.id) === '1033');
        const trackTypeDefs = lithologyCat?.trackTypes || [];
        const trackTypes = checkedLithologyTypes.map((id) => {
          const def = trackTypeDefs.find((t: any) => Number(t.id) === id);
          return { trackTypeId: id, displayName: def?.name || `Lithology ${id}` };
        });
        dispatch(addLithologyTrack({ trackTypes, boreholeIds: targetBoreholeIds }));
      } else if (trackType === 7) {
        // Text tracks
        const categories = trackTypesHierarchyData?.trackTypeCategories || [];
        const textCat = categories.find((c: any) => String(c.id) === '1038');
        const trackTypeDefs = textCat?.trackTypes || [];
        const trackTypes = checkedTextTypes.map((id) => {
          const def = trackTypeDefs.find((t: any) => Number(t.id) === id);
          return { trackTypeId: id, displayName: def?.name || `Text ${id}` };
        });
        dispatch(addTextTrack({ trackTypes, boreholeIds: targetBoreholeIds }));
      } else if (trackType === 9) {
        // Stratigraphy tracks
        const categories = trackTypesHierarchyData?.trackTypeCategories || [];
        const stratigraphyCat = categories.find((c: any) => String(c.id) === '1040');
        const trackTypeDefs = stratigraphyCat?.trackTypes || [];
        const trackTypes = checkedStratigraphyTypes.map((id) => {
          const def = trackTypeDefs.find((t: any) => Number(t.id) === id);
          return { trackTypeId: id, displayName: def?.name || `Stratigraphy ${id}` };
        });
        dispatch(addStratigraphyTrack({ trackTypes, boreholeIds: targetBoreholeIds }));
      } else if (trackType === 3) {
        // Cumulative — all selected track types become curves in a single track
        const selected = trackTypeOptions.filter((t: any) => effectiveIds.includes(Number(t.id)));
        dispatch(addCumulativeTrack({
          trackTypes: selected.map((t: any) => ({
            trackTypeId: Number(t.id),
            displayName: t.name
          })),
          boreholeIds: targetBoreholeIds
        }));
      } else if (trackType === 4) {
        // Tadpole — all selected track types become curves in a single track
        const selected = trackTypeOptions.filter((t: any) => effectiveIds.includes(Number(t.id)));
        dispatch(addTadpoleTrack({
          trackTypes: selected.map((t: any) => ({
            trackTypeId: Number(t.id),
            displayName: t.name
          })),
          boreholeIds: targetBoreholeIds
        }));
      } else {
        // Graph / tadpole / horizontal bar
        const selected = trackTypeOptions.filter((t: any) => effectiveIds.includes(Number(t.id)));
        dispatch(addGraphTracks({
          trackTypes: selected.map((t: any) => ({
            trackTypeId: Number(t.id),
            displayName: t.name,
            allowedCurveTypes: t.allowedCurveTypes,
            units: t.units
          })),
          boreholeIds: targetBoreholeIds
        }));
      }

      // Fetch scale metadata and track data directly, mirroring the view-mode
      // fetchViewDataForAddedTracks approach.  The unified useEffect is unreliable
      // here because isUnifiedFetchInFlight (a module-level flag) may be true when
      // the effect fires, causing it to exit early and never re-trigger.
      if (clientId && projectId && startDepth != null && endDepth != null && targetBoreholeIds.length > 0) {
        const isImageTrack = trackType === 1;
        const boreholesPayload = targetBoreholeIds.map((bId: number) => ({
          boreholeId: bId,
          projectId,
          trackTypeIds: isImageTrack ? [] : effectiveIds,
          imageTypeIds: isImageTrack ? effectiveIds : []
        }));
        // Stamp the skip flag so the unified effect doesn't double-fetch when it
        // fires due to the template.tracks / addedTrackTypeIds state changes.
        skipNextUnifiedFetchRef.current = true;
        setAddedTrackTypeIds([]); // cancel the earlier set so the effect path is idle
        setLastMultiTrackRequest({
          boreholeEntries: boreholesPayload,
          bufferedStart: startDepth,
          bufferedEnd: endDepth,
          pixelHeight: viewportHeight
        });
        setLastMultiTrackResponse(null);
        fetchViewScalesAndTrackDataDirect({
          clientId,
          boreholes: boreholesPayload,
          startDepth,
          endDepth,
          pixelHeight: viewportHeight,
          depthUnit: template?.depthUnit ?? null,
          cumulativeGroups
        }).then((result) => {
          dispatch(mergeDirectTrackData({
            groupTrackData: result.groupTrackData,
            scaleMetadataByBorehole: result.scaleMetadataByBorehole,
            groupTrackImages: result.groupTrackImages
          }));
        });
      }
    } else {
      // View mode: add tracks to the appropriate group(s)
      const tracksForView = effectiveIds.map((id) => {
        if (trackType === 1) {
          const imageType = (imageTypes?.imageTypes || []).find((t: any) => t.value === id || Number(t.value) === id);
          return { trackType: 1, imageType: id, trackTypeId: id, trackName: imageType?.name || `Image ${id}`, displayName: imageType?.name || `Image ${id}` };
        }
        if (trackType === 5) {
          const categories = trackTypesHierarchyData?.trackTypeCategories || [];
          const def = (categories.find((c: any) => String(c.id) === '1033')?.trackTypes || []).find((t: any) => Number(t.id) === id);
          return { trackType: 5, lithologyTypeId: id, trackTypeId: id, trackName: def?.name || `Lithology ${id}`, displayName: def?.name || `Lithology ${id}` };
        }
        if (trackType === 7) {
          const categories = trackTypesHierarchyData?.trackTypeCategories || [];
          const def = (categories.find((c: any) => String(c.id) === '1038')?.trackTypes || []).find((t: any) => Number(t.id) === id);
          return { trackType: 7, textTrackTypeId: id, trackTypeId: id, trackName: def?.name || `Text ${id}`, displayName: def?.name || `Text ${id}` };
        }
        if (trackType === 9) {
          const categories = trackTypesHierarchyData?.trackTypeCategories || [];
          const def = (categories.find((c: any) => String(c.id) === '1040')?.trackTypes || []).find((t: any) => Number(t.id) === id);
          return { trackType: 9, stratigraphyTrackTypeId: id, trackTypeId: id, trackName: def?.name || `Stratigraphy ${id}`, displayName: def?.name || `Stratigraphy ${id}` };
        }
        // Graph
        const t = trackTypeOptions.find((opt: any) => Number(opt.id) === id);
        return { trackType: 0, trackId: id, trackTypeId: id, trackName: t?.name || `Track ${id}`, displayName: t?.name || `Track ${id}`, allowedCurveTypes: t?.allowedCurveTypes, units: t?.units };
      });

      const resolveTargetBoreholeIds = () => {
        if (addTrackTargetBoreholeId !== 'all') {
          const targetId = Number(addTrackTargetBoreholeId);
          return isNaN(targetId) ? [] : [targetId];
        }
        return (viewData?.groups || []).map((g: any) => Number(g?.boreholeId)).filter((id: number) => !isNaN(id));
      };

      const fetchViewDataForAddedTracks = () => {
        if (!isViewMode || !clientId || !projectId) return;
        if (startDepth == null || endDepth == null) return;
        const targetBoreholeIds = resolveTargetBoreholeIds();
        if (targetBoreholeIds.length === 0) return;

        const trackTypeIdSet = new Set<number>();
        const imageTypeIdSet = new Set<number>();
        tracksForView.forEach((t: any) => {
          if (t?.trackType === 1) {
            const imageId = Number(t?.imageType ?? t?.trackTypeId);
            if (!isNaN(imageId)) imageTypeIdSet.add(imageId);
            return;
          }
          const trackTypeId = Number(t?.trackTypeId);
          if (!isNaN(trackTypeId)) trackTypeIdSet.add(trackTypeId);
        });

        const trackTypeIds = Array.from(trackTypeIdSet);
        const imageTypeIds = Array.from(imageTypeIdSet);
        if (trackTypeIds.length === 0 && imageTypeIds.length === 0) return;

        const boreholesPayload = targetBoreholeIds.map((bId: number) => ({
          boreholeId: bId,
          projectId,
          trackTypeIds,
          imageTypeIds
        }));

        skipNextUnifiedFetchRef.current = true;
        setLastMultiTrackRequest({
          boreholeEntries: boreholesPayload,
          bufferedStart: startDepth,
          bufferedEnd: endDepth,
          pixelHeight: viewportHeight
        });
        setLastMultiTrackResponse(null);

        fetchViewScalesAndDataDirect({
          boreholes: boreholesPayload,
          startDepth,
          endDepth
        });
      };

      if (addTrackTargetBoreholeId !== 'all') {
        const targetId = Number(addTrackTargetBoreholeId);
        const targetGroup = viewData?.groups?.find((g: any) => Number(g?.boreholeId) === targetId);
        if (targetGroup?.groupId) {
          if (trackType === 4) {
            const selected = trackTypeOptions
              .filter((t: any) => effectiveIds.includes(Number(t.id)))
              .map((t: any) => ({
                trackTypeId: Number(t.id),
                displayName: t.name
              }));
            const tadpoleLabel = selected.map((t: any) => t.displayName).join(', ');
            dispatch(addTracks({
              groupId: targetGroup.groupId,
              tracks: [{
                trackType: 4,
                trackName: tadpoleLabel,
                label: tadpoleLabel,
                trackId: selected.length > 0 ? selected[0].trackTypeId : null,
                boreholeId: targetGroup.boreholeId,
                boreholeName: targetGroup.boreholeName,
                projectName: targetGroup.projectName,
                curves: selected
              }]
            }));
          } else {
            dispatch(addTracks({ groupId: targetGroup.groupId, tracks: tracksForView }));
          }
          fetchViewDataForAddedTracks();
        } else {
          if (trackType === 4) {
            const selected = trackTypeOptions
              .filter((t: any) => effectiveIds.includes(Number(t.id)))
              .map((t: any) => ({
                trackTypeId: Number(t.id),
                displayName: t.name
              }));
            dispatch(addTadpoleTracksToGroups({ tracks: selected }));
          } else {
            dispatch(addGraphTracksToGroups({ tracks: tracksForView }));
          }
          fetchViewDataForAddedTracks();
        }
      } else {
        if (trackType === 4) {
          const selected = trackTypeOptions
            .filter((t: any) => effectiveIds.includes(Number(t.id)))
            .map((t: any) => ({
              trackTypeId: Number(t.id),
              displayName: t.name
            }));
          dispatch(addTadpoleTracksToGroups({ tracks: selected }));
        } else {
          dispatch(addGraphTracksToGroups({ tracks: tracksForView }));
        }
        fetchViewDataForAddedTracks();
      }
    }

    // Clear all checked lists
    setSelectedTrackTypeIds([]);
    setCheckedImageTypes([]);
    setCheckedLithologyTypes([]);
    setCheckedTextTypes([]);
    setCheckedStratigraphyTypes([]);
  };

  useEffect(() => {
    if (!isLoggedDepthTypeSelected) return;

    // Keep add-track panel consistent when switching depth type to Logged.
    if (TRACK_TYPES_HIDDEN_IN_LOGGED_DEPTH.has(selectedTemplateTrackType)) {
      setSelectedTemplateTrackType(0);
    }
    setCheckedLithologyTypes([]);
    setCheckedTextTypes([]);
    setCheckedStratigraphyTypes([]);
  }, [isLoggedDepthTypeSelected, selectedTemplateTrackType]);

  const onToggleBorehole = (id: number, checked: boolean) => {
    setPendingBoreholeIds((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, id]));
      }
      return prev.filter((bId) => bId !== id);
    });
  };

  const onConfirmBoreholes = () => {
    const next = pendingBoreholeIds.slice();
    const added = next.filter((id) => !selectedBoreholeIds.includes(id));
    setSelectedBoreholeIds(next);
    setAddedBoreholeIds(added.length > 0 ? added : []);
    setHasConfirmedBoreholes(true);
  };

  const onAddBoreholes = (boreholes: any[]) => {
    if (!boreholes || boreholes.length === 0) return;
    const existingIds = new Set((viewData?.groups || []).map((g: any) => Number(g?.boreholeId)));
    const boreholesToAdd = boreholes.filter((b: any) => !existingIds.has(Number(b?.id)));
    if (boreholesToAdd.length === 0) return;

    const groups = viewData?.groups || [];
    let tracksToAdd: any[] = [];
    const baseTracks = isViewMode
      ? (groups.length > 0 && Array.isArray(groups[0]?.tracks)
          ? groups[0].tracks
          : [])
      : ((template?.tracks && template.tracks.length > 0)
          ? template.tracks
          : (sortedTrackData || []));

    if (baseTracks.length > 0) {
      const getTrackKey = (track: any) => {
        if (track?.trackType === 1) {
          return `image:${Number(track?.imageType)}`;
        }
        const curveIds = (track?.curves || [])
          .map((c: any) => Number(c?.trackTypeId))
          .filter((id: number) => !isNaN(id));
        if (curveIds.length > 0) {
          const key = curveIds.sort((a: number, b: number) => a - b).join(',');
          return `curves:${key}`;
        }
        return `track:${Number(track?.trackTypeId)}`;
      };

      const groupTrackKeys = groups.map((g: any) => {
        const tracks = g?.tracks || [];
        return new Set(tracks.map(getTrackKey));
      });

      tracksToAdd = baseTracks
        .filter((track: any) => {
          const key = getTrackKey(track);
          return groupTrackKeys.every((set: Set<string>) => set.has(key));
        })
        .map((track: any) => ({
          ...track,
          datas: [],
          noData: true
        }));
    }

    const mapTracksForView = (borehole: any, baseTracks: any[]) => {
      return (baseTracks || []).map((track: any) => {
        const trackType = typeof track?.trackType === 'number' ? track.trackType : 0;
        const mappedCurves = Array.isArray(track.curves)
          ? track.curves.map((curve: any) => ({
            ...curve,
            boreholeId: borehole.id,
            boreholeName: borehole.name,
            projectName: borehole.project
          }))
          : track.curves;

        // Ensure lithology/text/stratigraphy tracks have their typeId properties populated
        let result: any = {
          ...track,
          boreholeId: borehole.id,
          boreholeName: borehole.name,
          projectName: borehole.project,
          trackName: track.trackName || track.displayName || track.label,
          trackId: track.trackId || track.id || track.trackTypeId || track.imageType,
          curves: mappedCurves
        };

        if (trackType === 5) {
          // Lithology track: ensure lithologyTypeId is set
          const lithologyTypeId = Number(
            track?.lithologyTypeId ?? track?.lithologyType ?? track?.trackTypeId
          );
          if (!isNaN(lithologyTypeId)) {
            result.lithologyTypeId = lithologyTypeId;
          }
        } else if (trackType === 7) {
          // Text track: ensure textTrackTypeId is set
          const textTrackTypeId = Number(track?.textTrackTypeId ?? track?.trackTypeId);
          if (!isNaN(textTrackTypeId)) {
            result.textTrackTypeId = textTrackTypeId;
          }
        } else if (trackType === 9) {
          // Stratigraphy track: ensure stratigraphyTrackTypeId is set
          const stratigraphyTrackTypeId = Number(
            track?.stratigraphyTrackTypeId ?? track?.trackTypeId
          );
          if (!isNaN(stratigraphyTrackTypeId)) {
            result.stratigraphyTrackTypeId = stratigraphyTrackTypeId;
          }
        }

        return result;
      });
    };

    const mappedBoreholes = boreholesToAdd.map((borehole: any) => {
      const boreholeIdNum = Number(borehole?.boreholeId ?? borehole?.id);
      return {
        ...borehole,
        boreholeId: !isNaN(boreholeIdNum) ? boreholeIdNum : borehole?.boreholeId,
        id: !isNaN(boreholeIdNum) ? boreholeIdNum : borehole?.id,
        tracks: mapTracksForView(borehole, tracksToAdd)
      };
    });

    dispatch(addBoreholes({ boreholes: mappedBoreholes }));

    const addedIds = mappedBoreholes.map((b: any) => Number(b.id)).filter((id: number) => !isNaN(id));
    setSelectedBoreholeIds((prev) => Array.from(new Set([...prev, ...addedIds])));
    setAddedBoreholeIds(addedIds);
    setHasConfirmedBoreholes(true);

    // When in view mode and Align Stratigraphic Surface is set to line, fetch offsets for the updated groups
    if (isViewMode && viewData?.topOffsetShowLine === true && viewData?.topOffsetStratigraphyTrackTypeId) {
      const allBoreholeIds = [...(viewData?.groups || []).map((g: any) => g.boreholeId), ...addedIds];
      dispatch(getBoreholeOffsetLines({
        stratigraphyTrackTypeId: viewData.topOffsetStratigraphyTrackTypeId,
        boreholeIds: allBoreholeIds
      }));
    }

    // Fetch scales/data for newly added boreholes directly (bypass redux pipeline)
    if (addedIds.length > 0 && clientId && projectId && startDepth != null && endDepth != null) {
      const depthRange = endDepth - startDepth;
      if (depthRange > 0) {
        const baseTrackIds = isViewMode
          ? (commonViewTrackTypeIds.length > 0 ? commonViewTrackTypeIds : trackTypeIds)
          : trackTypeIds;
        const scopedTrackIds = isViewMode
          ? baseTrackIds.filter((id) => !combinedNoDataSet.has(id))
          : baseTrackIds.slice();
        const boreholesPayload = addedIds.map((bId: number) => ({
          boreholeId: bId,
          projectId,
          trackTypeIds: scopedTrackIds,
          imageTypeIds
        })).filter((b) => b.trackTypeIds.length > 0 || b.imageTypeIds.length > 0);

        if (boreholesPayload.length > 0) {
          skipNextUnifiedFetchRef.current = true;
          setLastMultiTrackRequest({
            boreholeEntries: boreholesPayload,
            bufferedStart: startDepth,
            bufferedEnd: endDepth,
            pixelHeight: viewportHeight
          });
          setLastMultiTrackResponse(null);

          if (isViewMode) {
            fetchViewScalesAndDataDirect({
              boreholes: boreholesPayload,
              startDepth,
              endDepth
            }).finally(() => {
              setAddedBoreholeIds([]);
            });
          } else {
            fetchViewScalesAndTrackDataDirect({
              clientId,
              boreholes: boreholesPayload,
              startDepth,
              endDepth,
              pixelHeight: viewportHeight,
              depthUnit: template?.depthUnit ?? null,
              cumulativeGroups
            }).then((result) => {
              dispatch(mergeDirectTrackData({
                groupTrackData: result.groupTrackData,
                scaleMetadataByBorehole: result.scaleMetadataByBorehole,
                groupTrackImages: result.groupTrackImages
              }));
            }).finally(() => {
              setAddedBoreholeIds([]);
            });
          }
        } else {
          setAddedBoreholeIds([]);
        }
      }
    }
  };

  // Track reorder handlers
  const onUpdateTrackOrder = (sourceIndex: number, destinationIndex: number) => {
    dispatch(updateTrackOrder({ sourceIndex, destinationIndex }));
  };

  const onUpdateGroupTrackOrder = (sourceGroupId: any, destinationGroupId: any, sourceIndex: number, destinationIndex: number, applyToAll: boolean) => {
    dispatch(updateGroupTrackOrder({ sourceGroupId, destinationGroupId, sourceIndex, destinationIndex, applyToAll }));
  };

  const onUpdateGroupOrder = (sourceIndex: number, destinationIndex: number) => {
    dispatch(updateGroupOrder({ sourceIndex, destinationIndex }));
  };

  // Data editing handlers for lithography
  const onChangeDataValue = (id: number | undefined, trackTypeId: number, value: any, color: string, lithologyLexiconId: string, boreholeId: number, depth?: number) => {
    dispatch(changeDataValue({ id, depth, trackTypeId, value, color, lithologyLexiconId, showView: isViewMode, boreholeId }));
  };

  const onAddDataValue = (trackTypeId: any, depth: any, value: any, color: string, lithologyLexiconId: string, boreholeId: number): boolean => {
    const parsedDepth = Number(depth);
    if (!Number.isFinite(parsedDepth)) {
      return false;
    }

    const sourceTrack = isViewMode
      ? groupTrackData.find((t: any) => Number(t?.trackTypeId) === Number(trackTypeId) && Number(t?.boreholeId) === Number(boreholeId))
      : trackData.find((t: any) => Number(t?.trackTypeId) === Number(trackTypeId));
    const existsAtDepth = (sourceTrack?.data || []).some((d: any) => Number(d?.depth) === parsedDepth);
    if (existsAtDepth) {
      return false;
    }

    dispatch(addDataValue({ trackTypeId, depth: parsedDepth, value, color, lithologyLexiconId, showView: isViewMode, boreholeId }));
    return true;
  };

  const onDeleteDataValue = (trackTypeId: any, id: string, boreholeId: number) => {
    dispatch(deleteDataValue({ trackTypeId, id, showView: isViewMode, boreholeId }));
  };

  const onSaveLithologyData = (trackTypeId: any, boreholeId: number) => {
    const trackDataItem = isViewMode
      ? groupTrackData.find((t: any) => t.trackTypeId == trackTypeId && t.boreholeId === boreholeId)
      : trackData.find((t: any) => t.trackTypeId == trackTypeId);
    
    const data = { trackTypeId, data: trackDataItem?.data };
    dispatch(saveLithologyData({ clientId, projectId, boreholeId, data }));
  };

  // Data editing handlers for text tracks
  const onChangeTextData = (trackTypeId: any, data: any, boreholeId: number) => {
    dispatch(changeTextData({ trackTypeId, data, showView: isViewMode, boreholeId }));
  };

  const onAddTextValue = (textTrackTypeId: any, fromDepth: number, toDepth: number, value: any, boreholeId: number) => {
    // Validate that depth ranges don't overlap
    let track = isViewMode
      ? groupTrackData.find((t: any) => t.trackTypeId === textTrackTypeId && t.boreholeId === boreholeId)
      : trackData.find((t: any) => t.trackTypeId === textTrackTypeId);

    if (track?.data) {
      const fromDepthError = track.data.findIndex((d: any) => fromDepth >= d.fromDepth && fromDepth < d.toDepth) !== -1;
      const toDepthError = track.data.findIndex((d: any) => toDepth > d.fromDepth && toDepth < d.toDepth) !== -1;

      if (fromDepthError || toDepthError) {
        return false;
      }
    }

    dispatch(addTextValue({ textTrackTypeId, fromDepth, toDepth, value, showView: isViewMode, boreholeId }));
    return true;
  };

  const onSaveTextData = (trackTypeId: any, data: any, boreholeId: number) => {
    dispatch(saveTextData({ clientId, projectId, boreholeId, trackTypeId, data }));
  };

  // Data editing handlers for stratigraphy
  const onChangeStratigraphyData = (trackTypeId: any, data: any, boreholeId: number) => {
    dispatch(changeStratigraphyData({ trackTypeId, data, showView: isViewMode, boreholeId }));
  };

  const onAddStratigraphyValue = (stratigraphyTrackTypeId: any, fromDepth: number, toDepth: number, lexicon: any, boreholeId: number) => {
    // Validate that depth ranges don't overlap against the effective source of truth
    const sourceData = isViewMode ? groupTrackData : groupTrackData;
    let track = sourceData.find(
      (t: any) => Number(t.trackTypeId) === Number(stratigraphyTrackTypeId) && Number(t.boreholeId) === Number(boreholeId)
    );
    if (!track) {
      track = trackData.find((t: any) => t.trackTypeId === stratigraphyTrackTypeId);
    }

    if (track?.data) {
      const fromDepthError = track.data.findIndex((d: any) => fromDepth >= d.fromDepth && fromDepth < d.toDepth) !== -1;
      const toDepthError = track.data.findIndex((d: any) => toDepth > d.fromDepth && toDepth < d.toDepth) !== -1;

      if (fromDepthError || toDepthError) {
        return false;
      }
    }

    dispatch(addStratigraphyValue({
      stratigraphyTrackTypeId,
      fromDepth,
      toDepth,
      value: lexicon.description,
      stratigraphyLexiconId: lexicon.id,
      color: lexicon.color,
      showView: isViewMode,
      boreholeId
    }));

    // Expand the visible depth range if the new entry goes beyond the current end depth.
    // Suppress the re-fetch that would normally fire when endDepth changes — we don't
    // want a data reload to wipe the newly added (unsaved) row.
    if (endDepth !== null && toDepth > endDepth) {
      skipNextUnifiedFetchRef.current = true;
      setEndDepth(toDepth);
    }

    return true;
  };

  const onSaveChangeStratigraphyData = async (trackTypeId: any, data: any, boreholeId: number) => {
    try {
      await dispatch(saveStratigraphyData({ clientId, projectId, boreholeId, trackTypeId, data })).unwrap();

      // Refresh stratigraphic surface lines after successful save if in view mode
      if (isViewMode && viewData?.topOffsetShowLine && viewData?.topOffsetStratigraphyTrackTypeId && viewData?.groups) {
        const boreholeIds = viewData.groups.map((group: any) => group.boreholeId);
        // TODO: Add getBoreholeOffsetLines dispatch if available
        // dispatch(getBoreholeOffsetLines({
        //   stratigraphyTrackTypeId: viewData.topOffsetStratigraphyTrackTypeId,
        //   boreholeIds
        // }));
      }
    } catch (error) {
      console.error('Failed to save stratigraphy data:', error);
    }
  };

  // Depth change handler
  const onChangeDepth = (trackTypeId: any, id: string, newDepth: any) => {
    dispatch(changeDepth({ trackTypeId, id, newDepth }));
  };

  // Track edit mode handler
  const onSetTrackEditMode = (trackId: any, editMode: boolean) => {
    dispatch(setTrackEditMode({ trackId, editMode }));
  };

  useEffect(() => {
    // For view mode, use depth range from multi-borehole response (minimumDepth/maximumDepth)
    // For template mode, use depth range from single borehole summary
    if (isViewMode) {
      if (viewData?.minimumDepth != null && viewData?.maximumDepth != null) {
        setStartDepth(viewData.minimumDepth);
        setEndDepth(viewData.maximumDepth);
        return;
      }

      if (Array.isArray(viewData?.groups) && viewData.groups.length > 0) {
        const minFromGroups = Math.min(
          ...viewData.groups
            .map((g: any) => Number(g?.minimumDepth))
            .filter((v: number) => !isNaN(v))
        );
        const maxFromGroups = Math.max(
          ...viewData.groups
            .map((g: any) => Number(g?.maximumDepth))
            .filter((v: number) => !isNaN(v))
        );
        if (!isNaN(minFromGroups) && !isNaN(maxFromGroups)) {
          setStartDepth(minFromGroups);
          setEndDepth(maxFromGroups);
          return;
        }
      }
    }

    if (boreholeData?.depthTypes?.[depthType]) {
      const minDepth = boreholeData.depthTypes[depthType]?.minDepth ?? 0;
      const maxDepth = boreholeData.depthTypes[depthType]?.maxDepth ?? 0;
      setStartDepth(minDepth);
      setEndDepth(maxDepth);
    }
  }, [isViewMode, viewData?.minimumDepth, viewData?.maximumDepth, viewData?.groups, boreholeData, depthType]);

  // Extend depth range when scales return min/max beyond current range.
  // Uses a ref-captured snapshot of the current depths to avoid stale closures and
  // to prevent the effect from re-triggering on the depth changes it causes itself.
  // Stable color cache for group headers – keys are borehole IDs (as strings).
  // Colors are assigned on first use and never change, even when useMemo re-runs.
  const groupColorCacheRef = useRef<Record<string, string>>({});

  const startDepthRef = useRef<number | null>(null);
  const endDepthRef   = useRef<number | null>(null);
  startDepthRef.current = startDepth;
  endDepthRef.current   = endDepth;

  useEffect(() => {
    if (isViewMode) return; // view mode sets its own depths from viewData
    if (templateMinDepth === null && templateMaxDepth === null) return;
    const curStart = startDepthRef.current;
    const curEnd   = endDepthRef.current;
    if (curStart == null || curEnd == null) return;

    const newStart = (templateMinDepth !== null && templateMinDepth < curStart) ? templateMinDepth : curStart;
    const newEnd   = (templateMaxDepth !== null && templateMaxDepth > curEnd)   ? templateMaxDepth : curEnd;
    if (newStart >= curStart && newEnd <= curEnd) return; // nothing to extend

    const oldRange = curEnd - curStart;
    const newRange = newEnd - newStart;
    // Only trigger a full scales re-fetch when the range grows significantly
    if (newRange < DEPTH_REFETCH_GROWTH_FACTOR * oldRange) {
      skipNextUnifiedFetchRef.current = true;
    }
    if (newStart < curStart) setStartDepth(newStart);
    if (newEnd   > curEnd)   setEndDepth(newEnd);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateMinDepth, templateMaxDepth, isViewMode]);

  const { trackTypeIds, imageTypeIds } = useMemo(() => {
    const ids = new Set<number>();
    const imageIds = new Set<number>();

    // In view mode, extract from viewData.groups
    if (isViewMode && viewData?.groups) {
      viewData.groups.forEach((group: any) => {
        group?.tracks?.forEach((track: any) => {
          if (track?.trackType === 1 && typeof track?.imageType === 'number') {
            imageIds.add(track.imageType);
          }

          if (Array.isArray(track?.curves)) {
            track.curves.forEach((curve: any) => {
              const ttId = Number(curve?.trackTypeId);
              if (!isNaN(ttId) && ttId !== 0) {
                ids.add(ttId);
              }
            });
          }

          const possibleIds = [
            Number(track?.trackTypeId),
            Number(track?.lithologyType),
            Number(track?.lithologyTypeId),
            Number(track?.stratigraphyTrackTypeId),
            Number(track?.textTrackTypeId)
          ];

          possibleIds.forEach((id) => {
            if (!isNaN(id) && id !== 0) {
              ids.add(id);
            }
          });
        });
      });
    } else {
      // In template mode, extract from template.tracks
      template?.tracks?.forEach((track: any) => {
        if (track?.trackType === 1 && typeof track?.imageType === 'number') {
          imageIds.add(track.imageType);
        }

        if (Array.isArray(track?.curves)) {
          track.curves.forEach((curve: any) => {
            const ttId = Number(curve?.trackTypeId);
            if (!isNaN(ttId) && ttId !== 0) {
              ids.add(ttId);
            }
          });
        }

        const possibleIds = [
          Number(track?.trackTypeId),
          Number(track?.lithologyType),
          Number(track?.lithologyTypeId),
          Number(track?.stratigraphyTrackTypeId),
          Number(track?.textTrackTypeId)
        ];

        possibleIds.forEach((id) => {
          if (!isNaN(id) && id !== 0) {
            ids.add(id);
          }
        });
      });
    }

    return {
      trackTypeIds: Array.from(ids),
      imageTypeIds: Array.from(imageIds)
    };
  }, [isViewMode, viewData, template]);

  const multiTrackTypeIdsByBorehole = useMemo(() => {
    const map: { [boreholeId: number]: number[] } = {};
    if (isViewMode) {
      return viewTrackTypeIdsByBorehole;
    }
    selectedBoreholeIds.forEach((id) => {
      map[id] = trackTypeIds.slice();
    });
    return map;
  }, [isViewMode, viewTrackTypeIdsByBorehole, selectedBoreholeIds, trackTypeIds]);

  const sortedTrackData = useMemo(() => {
    if (!template?.tracks || !boreholeId) return [] as any[];
    const boreholeIdNum = Number(boreholeId);
    const dataToUse = isViewMode
      ? (Array.isArray(groupTrackData)
        ? groupTrackData.filter((entry: any) => Number(entry?.boreholeId) === boreholeIdNum)
        : [])
      : (Array.isArray(trackData) ? trackData : []);
    
    // Build a map of trackTypeId -> groupTrackData entry for quick lookup
    const groupTrackDataMap: { [trackTypeId: number]: any } = {};
    (groupTrackData || []).forEach((entry: any) => {
      const bId = Number(entry?.boreholeId);
      const ttId = Number(entry?.trackTypeId);
      if (bId === boreholeIdNum && !isNaN(ttId)) {
        groupTrackDataMap[ttId] = entry;
      }
    });
    
    return (template.tracks || [])
      .map((t: any) => {
        const trackType = typeof t?.trackType === 'number' ? t.trackType : 0;

        if (isLoggedDepthTypeSelected && TRACK_TYPES_HIDDEN_IN_LOGGED_DEPTH.has(trackType)) {
          return null;
        }
        
        // Image tracks
        if (trackType === 1) {
          const imageType = Number(t?.imageType);
          const imageEntry = (groupTrackImages || []).find((ti: any) => 
            Number(ti?.imageType) === imageType && Number(ti?.boreholeId) === boreholeIdNum
          );
          return {
            ...t,
            trackType,
            boreholeId: boreholeIdNum,
            imageType,
            label: t?.displayName || t?.trackName,
            legend: t?.legend,
            legendRollover: t?.legendRollover,
            noImage: imageEntry?.noImage ?? true,
            hasDeepZoom: imageEntry?.hasDeepZoom ?? false,
            needsRegenerating: imageEntry?.needsRegenerating ?? false,
            depths: imageEntry?.depths,
            isCuttings: imageEntry?.isCuttings,
            useStrips: imageEntry?.useStrips ?? t?.useStrips ?? false
          };
        }
        
        // Lithology, text, and stratigraphy tracks
        if (trackType === 5 || trackType === 7 || trackType === 9) {
          const trackTypeId = Number(
            trackType === 5
              ? (t?.lithologyTypeId ?? t?.lithologyType ?? t?.trackTypeId)
              : trackType === 7
                ? (t?.textTrackTypeId ?? t?.trackTypeId)
                : (t?.stratigraphyTrackTypeId ?? t?.trackTypeId)
          );
          const entry = !isNaN(trackTypeId)
            ? (trackType === 5
              ? dataToUse.find((d: any) => Number(d?.trackTypeId) === trackTypeId)
              : groupTrackDataMap[trackTypeId])
            : undefined;
          return {
            ...t,
            trackTypeId,
            trackType,
            boreholeId: boreholeIdNum,
            label: t?.displayName || t?.trackName,
            lithologyTypeId: trackType === 5 ? trackTypeId : t?.lithologyTypeId,
            textTrackTypeId: trackType === 7 ? trackTypeId : t?.textTrackTypeId,
            stratigraphyTrackTypeId: trackType === 9 ? trackTypeId : t?.stratigraphyTrackTypeId,
            datas: entry ? [entry] : [],
            noData: entry?.noData ?? true,
            codesAndColors: trackType === 5 && entry?.data?.length > 0
              ? [...new Map(entry.data.map((item: any) =>
                  [item["value"], { code: item.value, color: item.color }])).values()].filter((c: any) => c.code)
              : (t?.codesAndColors || []),
            showGridlines: t?.showGridlines,
            showValueAxisAnnotation: t?.showValueAxisAnnotation,
            scaleType: t?.scaleType,
            trackEditMode: t?.trackEditMode ?? true,
            isSelected: selectedTrack?.id === t?.id
          };
        }
        
        // Graph, cumulative, type 4, and horizontal bar tracks (original logic)
        if (trackType !== 0 && trackType !== 3 && trackType !== 4 && trackType !== 8) return null;
        
        // Handle tracks without curves (still include them)
        if (!Array.isArray(t?.curves) || t.curves.length === 0) {
          return {
            id: t.id,
            data: []
          };
        }
                
        const curveTrackTypeIds = t.curves.map((curve: any) => curve.trackTypeId);
        const trackTrackDatas = dataToUse.filter((d: any) => curveTrackTypeIds.includes(d.trackTypeId));
        const curves = t.curves.map((curve: any) => {
          const curveTrackData = trackTrackDatas.find((d: any) => d.trackTypeId === curve.trackTypeId);
          return {
            ...curve,
            boreholeId: boreholeIdNum,
            noData: curveTrackData?.noData ?? true,
            isAverage: curveTrackData?.isAverage ?? false,
            scale: curveTrackData?.scale ?? null,
            isCuttings: curveTrackData?.isCuttings ?? false
          };
        });
        const hasData = trackTrackDatas.length > 0 && !trackTrackDatas.every((d: any) => d.noData);
        return {
          ...t,
          boreholeId: boreholeIdNum,
          curves,
          label: curves.map((curve: any) => curve.displayName).join(', '),
          noData: !hasData,
          trackType: t.trackType,
          scale: trackTrackDatas[0]?.scale,
          isAverage: trackTrackDatas[0]?.isAverage,
          isCuttings: trackTrackDatas[0]?.isCuttings,
          datas: trackTrackDatas,
          minValue: trackTrackDatas[0]?.minValue,
          maxValue: trackTrackDatas[0]?.maxValue,
          showGridlines: t.showGridlines,
          showValueAxisAnnotation: t.showValueAxisAnnotation,
          scaleType: t.scaleType,
          collapsed: t.collapsed,
          isSelected: selectedTrack?.id === t.id,
          scrollIntoView: t.scrollIntoView,
          scrollAdjustmentIntoView: t.scrollAdjustmentIntoView
        };
      })
      .filter(Boolean) as any[];
  }, [template?.tracks, isViewMode, trackData, boreholeId, groupTrackData, groupTrackImages, selectedTrack, isLoggedDepthTypeSelected]);

  const trackOrderData = useMemo(() => {
    const labelById = new Map((sortedTrackData || []).map((t: any) => [t.id, t.label]));
    return (template?.tracks || [])
      .filter((track: any) => {
        const trackType = typeof track?.trackType === 'number' ? track.trackType : 0;
        return !isLoggedDepthTypeSelected || !TRACK_TYPES_HIDDEN_IN_LOGGED_DEPTH.has(trackType);
      })
      .map((track: any) => {
      const fallbackId = Number(
        track?.trackTypeId ??
        track?.lithologyTypeId ??
        track?.stratigraphyTrackTypeId ??
        track?.textTrackTypeId
      );
      const fallbackLabel = !isNaN(fallbackId)
        ? trackTypeNameById[fallbackId]
        : undefined;
      return {
        ...track,
        label: labelById.get(track.id) || track?.displayName || track?.trackName || fallbackLabel || `Track ${track.id}`
      };
    });
  }, [template?.tracks, sortedTrackData, trackTypeNameById, isLoggedDepthTypeSelected]);


  const trackInstances = useMemo(() => {
    const instances: Array<{ instanceId: string; trackTypeId: number; boreholeId?: number }> = [];
    if (isViewMode && viewData?.groups) {
      viewData.groups.forEach((group: any, gIndex: number) => {
        const boreholeIdNum = Number(group?.boreholeId);
        group?.tracks?.forEach((track: any, tIndex: number) => {
          if (Array.isArray(track?.curves) && track.curves.length > 0) {
            track.curves.forEach((curve: any, cIndex: number) => {
              const ttId = Number(curve?.trackTypeId);
              if (!isNaN(ttId) && ttId !== 0) {
                instances.push({
                  instanceId: `view-${gIndex}-${tIndex}-curve-${curve?.id || cIndex}`,
                  trackTypeId: ttId,
                  boreholeId: !isNaN(boreholeIdNum) ? boreholeIdNum : undefined
                });
              }
            });
          } else {
            const ttId = Number(track?.trackTypeId);
            if (!isNaN(ttId) && ttId !== 0) {
              instances.push({
                instanceId: `view-${gIndex}-${tIndex}-track-${ttId}`,
                trackTypeId: ttId,
                boreholeId: !isNaN(boreholeIdNum) ? boreholeIdNum : undefined
              });
            }
          }
        });
      });
      return instances;
    }

    (template?.tracks || []).forEach((track: any, index: number) => {      
      if (Array.isArray(track?.curves) && track.curves.length > 0) {
        track.curves.forEach((curve: any, cIndex: number) => {
          const ttId = Number(curve?.trackTypeId);
          if (!isNaN(ttId) && ttId !== 0) {
            instances.push({
              instanceId: `${track?.id || index}-curve-${curve?.id || cIndex}`,
              trackTypeId: ttId,
              boreholeId: typeof curve?.boreholeId === 'number' ? curve.boreholeId : undefined
            });
          }
        });
      } else {
        const ttId = Number(track?.trackTypeId);
        if (!isNaN(ttId) && ttId !== 0) {
          instances.push({
            instanceId: `${track?.id || index}-track-${ttId}`,
            trackTypeId: ttId,
            boreholeId: typeof track?.boreholeId === 'number' ? track.boreholeId : undefined
          });
        }
      }
    });
    return instances;
  }, [template, isViewMode, viewData]);

  const effectiveScaleMetadataByBorehole = useMemo(() => {
    return isViewMode && Object.keys(directScaleMetadataByBorehole).length > 0
      ? directScaleMetadataByBorehole
      : scaleMetadataByBorehole;
  }, [isViewMode, directScaleMetadataByBorehole, scaleMetadataByBorehole]);

  const effectiveScaleMetadata = useMemo(() => {
    if (!isViewMode || Object.keys(directScaleMetadataByBorehole).length === 0) {
      return scaleMetadata;
    }
    const flattened: any[] = [];
    Object.values(directScaleMetadataByBorehole).forEach((arr: any) => {
      if (Array.isArray(arr)) flattened.push(...arr);
    });
    return flattened;
  }, [isViewMode, directScaleMetadataByBorehole, scaleMetadata]);

  const effectiveGroupTrackImages = useMemo(() => {
    return isViewMode && directGroupTrackImages.length > 0
      ? directGroupTrackImages
      : groupTrackImages;
  }, [isViewMode, directGroupTrackImages, groupTrackImages]);

  const effectiveGroupTrackData = useMemo(() => {
    if (!isViewMode || directGroupTrackData.length === 0) return groupTrackData;
    // directGroupTrackData has chart-optimised data for numeric tracks but typically
    // does NOT include stratigraphy / text track entries (they are loaded separately
    // into Redux groupTrackData). Merge: direct data wins for entries that exist in
    // both, but supplement with any Redux entries not present in direct data so that
    // stratigraphy / text edits are always visible in the adjustments panel.
    // Additionally, if the Redux entry for the same track has unsaved (negative-ID)
    // rows, inject them into the direct entry so they survive depth-range re-fetches.
    const directKeys = new Set(
      directGroupTrackData.map((e: any) => `${Number(e.boreholeId)}-${Number(e.trackTypeId)}`)
    );
    const reduxByKey = new Map<string, any>(
      groupTrackData.map((e: any) => [`${Number(e.boreholeId)}-${Number(e.trackTypeId)}`, e])
    );
    const mergedDirect = directGroupTrackData.map((directEntry: any) => {
      const key = `${Number(directEntry.boreholeId)}-${Number(directEntry.trackTypeId)}`;
      const reduxEntry = reduxByKey.get(key);
      if (!reduxEntry) return directEntry;
      const unsavedRows = (reduxEntry.data || []).filter(
        (row: any) => typeof row.id === 'number' && row.id < 0
      );
      if (unsavedRows.length === 0) return directEntry;
      const mergedData = [...(directEntry.data || []), ...unsavedRows]
        .sort((a: any, b: any) => (a.depth ?? a.fromDepth ?? 0) - (b.depth ?? b.fromDepth ?? 0));
      return { ...directEntry, data: mergedData };
    });
    const reduxOnly = groupTrackData.filter(
      (e: any) => !directKeys.has(`${Number(e.boreholeId)}-${Number(e.trackTypeId)}`)
    );
    return reduxOnly.length > 0 ? [...mergedDirect, ...reduxOnly] : mergedDirect;
  }, [isViewMode, directGroupTrackData, groupTrackData]);

  const displayTrackInstances = useMemo(() => {
    if (isViewMode && trackInstances.length > 0) return trackInstances;
    if (isMultiBoreholeMode) {
      const merged = new Map<string, { instanceId: string; trackTypeId: number; boreholeId?: number }>();
      const targetBoreholeIds = selectedBoreholeIds.length > 0
        ? selectedBoreholeIds
        : (isViewMode ? viewBoreholeIds : (boreholeId ? [Number(boreholeId)] : []));

      groupTrackData.forEach((entry: any) => {
        const bId = Number(entry?.boreholeId);
        const ttId = Number(entry?.trackTypeId);
        if (isNaN(bId) || isNaN(ttId)) return;
        const key = `${bId}-${ttId}`;
        merged.set(key, {
          instanceId: `group-${bId}-${ttId}`,
          trackTypeId: ttId,
          boreholeId: bId
        });
      });

      targetBoreholeIds.forEach((bId: number) => {
        const perBoreholeMeta = effectiveScaleMetadataByBorehole[String(bId)] || [];
        const metaArr = perBoreholeMeta.length > 0 ? perBoreholeMeta : (effectiveScaleMetadata || []);
        metaArr.forEach((m: any) => {
          const ttId = Number(m?.trackTypeId);
          if (isNaN(ttId)) return;
          const key = `${bId}-${ttId}`;
          if (!merged.has(key)) {
            merged.set(key, {
              instanceId: `meta-${bId}-${ttId}`,
              trackTypeId: ttId,
              boreholeId: bId
            });
          }
        });
      });

      return Array.from(merged.values());
    }
    if (trackInstances.length > 0) return trackInstances;
    if (!isViewMode && trackData.length > 0) {
      const bhId = boreholeId ? Number(boreholeId) : undefined;
      return trackData
        .map((entry: any, index: number) => ({
          instanceId: `track-${entry?.trackTypeId || index}`,
          trackTypeId: Number(entry?.trackTypeId),
          boreholeId: bhId
        }))
        .filter((entry: any) => !isNaN(entry.trackTypeId));
    }
    if (!isViewMode || groupTrackData.length === 0) return trackInstances;
    return groupTrackData
      .map((entry: any) => ({
        instanceId: `group-${entry?.boreholeId}-${entry?.trackTypeId}`,
        trackTypeId: Number(entry?.trackTypeId),
        boreholeId: Number(entry?.boreholeId)
      }))
      .filter((entry: any) => !isNaN(entry.trackTypeId) && !isNaN(entry.boreholeId));
  }, [
    isMultiBoreholeMode,
    isViewMode,
    selectedBoreholeIds,
    viewBoreholeIds,
    boreholeId,
    scaleMetadataByBorehole,
    scaleMetadata,
    groupTrackData,
    trackInstances,
    trackData
  ]);

  const trackTypeIdsKey = useMemo(() => trackTypeIds.slice().sort((a, b) => a - b).join(','), [trackTypeIds]);
  const imageTypeIdsKey = useMemo(() => imageTypeIds.slice().sort((a, b) => a - b).join(','), [imageTypeIds]);

  const imageTrackInstances = useMemo(() => {
    const instances: Array<{ instanceId: string; imageTypeId: number; boreholeId?: number }> = [];
    if (isViewMode && viewData?.groups) {
      viewData.groups.forEach((group: any, gIndex: number) => {
        const boreholeIdNum = Number(group?.boreholeId);
        group?.tracks?.forEach((track: any, tIndex: number) => {
          if (track?.trackType === 1 && typeof track?.imageType === 'number') {
            instances.push({
              instanceId: `view-img-${gIndex}-${tIndex}-${track.imageType}`,
              imageTypeId: Number(track.imageType),
              boreholeId: !isNaN(boreholeIdNum) ? boreholeIdNum : undefined
            });
          }
        });
      });
      return instances;
    }

    (template?.tracks || []).forEach((track: any, index: number) => {
      if (track?.trackType === 1 && typeof track?.imageType === 'number') {
        instances.push({
          instanceId: `img-${track?.id || index}-${track.imageType}`,
          imageTypeId: Number(track.imageType),
          boreholeId: typeof track?.boreholeId === 'number' ? track.boreholeId : undefined
        });
      }
    });

    return instances;
  }, [template, isViewMode, viewData]);

  const displayImageTrackInstances = useMemo(() => {
    if (imageTrackInstances.length > 0) return imageTrackInstances;
    const imagesSource = effectiveGroupTrackImages.length > 0 ? effectiveGroupTrackImages : trackImages;
    if (!imagesSource.length) return imageTrackInstances;
    return imagesSource
      .map((entry: any, index: number) => ({
        instanceId: `img-data-${entry?.boreholeId || 'single'}-${entry?.imageType || index}`,
        imageTypeId: Number(entry?.imageType),
        boreholeId: typeof entry?.boreholeId === 'number' ? entry.boreholeId : undefined
      }))
      .filter((entry: any) => !isNaN(entry.imageTypeId));
  }, [imageTrackInstances, effectiveGroupTrackImages, trackImages]);

  const imageNameById = useMemo(() => {
    const map: { [id: number]: string } = {};
    (template?.tracks || []).forEach((track: any) => {
      if (track?.trackType === 1 && typeof track?.imageType === 'number') {
        map[Number(track.imageType)] = track?.displayName || `Image ${track.imageType}`;
      }
    });
    if (isViewMode && viewData?.groups) {
      viewData.groups.forEach((group: any) => {
        group?.tracks?.forEach((track: any) => {
          if (track?.trackType === 1 && typeof track?.imageType === 'number') {
            map[Number(track.imageType)] = track?.displayName || track?.trackName || `Image ${track.imageType}`;
          }
        });
      });
    }
    return map;
  }, [template, isViewMode, viewData]);

  const scaleMetadataMap = useMemo(() => {
    const map: { [trackTypeId: number]: any } = {};
    (effectiveScaleMetadata || []).forEach((meta: any) => {
      if (typeof meta?.trackTypeId === 'number') {
        map[meta.trackTypeId] = meta;
      }
    });
    return map;
  }, [effectiveScaleMetadata]);

  const groupTrackDataMap = useMemo(() => {
    const map: { [boreholeId: number]: { [trackTypeId: number]: any } } = {};
    (effectiveGroupTrackData || []).forEach((entry: any) => {
      const bId = Number(entry?.boreholeId);
      const ttId = Number(entry?.trackTypeId);
      if (isNaN(bId) || isNaN(ttId)) return;
      if (!map[bId]) {
        map[bId] = {};
      }
      map[bId][ttId] = entry;
    });
    return map;
  }, [effectiveGroupTrackData]);

  const viewSortedGroupTrackData = useMemo(() => {
    // Multi-borehole template mode: create groups from selectedBoreholeIds and template
    if (!isViewMode && isMultiBoreholeMode && template?.tracks) {
      const targetBoreholeIds = selectedBoreholeIds.length > 0 ? selectedBoreholeIds : [];
      if (targetBoreholeIds.length === 0) return [] as any[];

      // Build a lookup from boreholeId → viewData group so we can pull topOffset and
      // stratigraphyLexiconStartAndEnds that are populated by the offset API calls
      const viewGroupByBoreholeId = new Map<number, any>();
      if (Array.isArray(viewData?.groups)) {
        viewData.groups.forEach((g: any) => {
          const bId = Number(g?.boreholeId);
          if (!isNaN(bId)) viewGroupByBoreholeId.set(bId, g);
        });
      }
      
      return targetBoreholeIds.map((boreholeIdNum: number) => {
        const boreholeInfo = boreholeOptions.find((b: { id: number; name: string }) => b.id === boreholeIdNum);
        const viewGroup = viewGroupByBoreholeId.get(boreholeIdNum);
        const stableGroupId = String(viewGroup?.groupId ?? boreholeIdNum);
        if (viewGroup?.color) {
          groupColorCacheRef.current[stableGroupId] = viewGroup.color;
        } else if (!groupColorCacheRef.current[stableGroupId]) {
          groupColorCacheRef.current[stableGroupId] = getRandomColor();
        }
        const groupTracks = (template.tracks || [])
          .map((track: any) => {
            const trackType = typeof track?.trackType === 'number' ? track.trackType : 0;
            if (isLoggedDepthTypeSelected && TRACK_TYPES_HIDDEN_IN_LOGGED_DEPTH.has(trackType)) {
              return null;
            }
            const baseTrack = {
              ...track,
              trackType,
              boreholeId: boreholeIdNum,
              trackWidth: track?.trackWidth ?? 150,
              displayOrder: track?.displayOrder,
              scrollIntoView: track?.scrollIntoView,
              scrollAdjustmentIntoView: track?.scrollAdjustmentIntoView
            };

            if (trackType === 1) {
              const imageType = Number(track?.imageType);
              const imageEntry = (effectiveGroupTrackImages || []).find((ti: any) => 
                Number(ti?.imageType) === imageType && Number(ti?.boreholeId) === boreholeIdNum
              );
              return {
                ...baseTrack,
                imageType,
                label: track?.displayName || track?.trackName,
                legend: track?.legend,
                legendRollover: track?.legendRollover,
                noImage: imageEntry?.noImage ?? true,
                hasDeepZoom: imageEntry?.hasDeepZoom ?? false,
                needsRegenerating: imageEntry?.needsRegenerating ?? false,
                depths: imageEntry?.depths,
                isCuttings: imageEntry?.isCuttings,
                useStrips: imageEntry?.useStrips ?? track?.useStrips ?? false
              };
            }

            if (trackType === 5 || trackType === 7 || trackType === 9) {
              const trackTypeId = Number(
                trackType === 5
                  ? (track?.lithologyTypeId ?? track?.lithologyType ?? track?.trackTypeId)
                  : trackType === 7
                    ? (track?.textTrackTypeId ?? track?.trackTypeId)
                    : (track?.stratigraphyTrackTypeId ?? track?.trackTypeId)
              );
              const entry = !isNaN(trackTypeId)
                ? groupTrackDataMap[boreholeIdNum]?.[trackTypeId]
                : undefined;
              return {
                ...baseTrack,
                trackTypeId,
                label: track?.displayName || track?.trackName,
                lithologyTypeId: trackType === 5 ? trackTypeId : track?.lithologyTypeId,
                textTrackTypeId: trackType === 7 ? trackTypeId : track?.textTrackTypeId,
                stratigraphyTrackTypeId: trackType === 9 ? trackTypeId : track?.stratigraphyTrackTypeId,
                datas: entry ? [entry] : [],
                noData: entry?.noData ?? true,
                codesAndColors: track?.codesAndColors || [],
                showGridlines: track?.showGridlines,
                showValueAxisAnnotation: track?.showValueAxisAnnotation,
                scaleType: track?.scaleType,
                isSelected: selectedTrack?.id === track?.id
              };
            }

            if (trackType === 0 || trackType === 3 || trackType === 4 || trackType === 8) {
              let curves: any[] = [];
              if (Array.isArray(track?.curves) && track.curves.length > 0) {
                curves = track.curves
                  .map((curve: any) => {
                    const ttId = Number(curve?.trackTypeId);
                    if (isNaN(ttId)) return null;
                    const entry = groupTrackDataMap[boreholeIdNum]?.[ttId];
                    return {
                      ...curve,
                      trackTypeId: ttId,
                      boreholeId: boreholeIdNum,
                      displayName: curve?.displayName || track?.displayName || track?.trackName,
                      noData: entry?.noData ?? true,
                      isAverage: entry?.isAverage ?? false,
                      scale: entry?.scale ?? null,
                      isCuttings: entry?.isCuttings ?? false
                    };
                  })
                  .filter(Boolean) as any[];
              } else {
                const ttId = Number(track?.trackTypeId);
                if (!isNaN(ttId)) {
                  const entry = groupTrackDataMap[boreholeIdNum]?.[ttId];
                  curves = [{
                    ...track,
                    trackTypeId: ttId,
                    boreholeId: boreholeIdNum,
                    displayName: track?.displayName || track?.trackName,
                    noData: entry?.noData ?? true,
                    isAverage: entry?.isAverage ?? false,
                    scale: entry?.scale ?? null,
                    isCuttings: entry?.isCuttings ?? false
                  }];
                }
              }

              const trackTypeIds = curves.map((c: any) => Number(c.trackTypeId)).filter((id: number) => !isNaN(id));
              const trackTrackDatas = trackTypeIds
                .map((id: number) => groupTrackDataMap[boreholeIdNum]?.[id])
                .filter(Boolean) as any[];
              const hasData = trackTrackDatas.length > 0 && !trackTrackDatas.every((d: any) => d.noData);

              const builtTrack = {
                ...baseTrack,
                curves,
                label: curves.map((curve: any) => curve.displayName).join(', '),
                noData: !hasData,
                datas: trackTrackDatas,
                scale: trackTrackDatas[0]?.scale,
                isAverage: trackTrackDatas[0]?.isAverage,
                isCuttings: trackTrackDatas[0]?.isCuttings,
                minValue: trackTrackDatas[0]?.minValue,
                maxValue: trackTrackDatas[0]?.maxValue,
                showGridlines: track?.showGridlines,
                showValueAxisAnnotation: track?.showValueAxisAnnotation,
                scaleType: track?.scaleType,
                collapsed: track?.collapsed,
                isSelected: selectedTrack?.id === track?.id
              };
              return builtTrack;
            }

            return null;
          })
          .filter(Boolean) as any[];

        return {
          groupId: stableGroupId,
          groupName: boreholeInfo?.name || `Borehole ${boreholeIdNum}`,
          color: groupColorCacheRef.current[stableGroupId],
          topOffset: viewGroup?.topOffset ?? 0,
          topOffsetType: viewGroup?.topOffsetType,
          maximumDepth: boreholeInfo?.maxDepth ?? maxDepth,
          boreholeId: boreholeIdNum,
          collapsed: viewGroup?.collapsed ?? false,
          boreholeDoesNotContainStratigraphy: viewGroup?.boreholeDoesNotContainStratigraphy,
          stratigraphyLexiconStartAndEnds: viewGroup?.stratigraphyLexiconStartAndEnds,
          tracks: groupTracks
        };
      });
    }

    // View mode: use viewData groups
    if (!isViewMode || !viewData?.groups) return [] as any[];

    return (viewData.groups || []).map((group: any) => {
      const boreholeIdNum = Number(group?.boreholeId);
      const boreholeInfo = boreholeOptions.find((b: { id: number; name: string }) => b.id === boreholeIdNum);
      const groupTracks = (group?.tracks || [])
        .map((track: any) => {
          const trackType = typeof track?.trackType === 'number' ? track.trackType : 0;
          if (isLoggedDepthTypeSelected && TRACK_TYPES_HIDDEN_IN_LOGGED_DEPTH.has(trackType)) {
            return null;
          }
          const baseTrack = {
            ...track,
            trackType,
            boreholeId: !isNaN(boreholeIdNum) ? boreholeIdNum : undefined,
            trackWidth: track?.trackWidth ?? 150,
            displayOrder: track?.displayOrder,
            scrollIntoView: track?.scrollIntoView,
            scrollAdjustmentIntoView: track?.scrollAdjustmentIntoView
          };

          if (trackType === 1) {
            const imageType = Number(track?.imageType);
              const imageEntry = !isNaN(boreholeIdNum)
              ? (effectiveGroupTrackImages || []).find((ti: any) => Number(ti?.imageType) === imageType && Number(ti?.boreholeId) === boreholeIdNum)
              : undefined;
            return {
              ...baseTrack,
              imageType,
              label: track?.displayName || track?.trackName,
              legend: track?.legend,
              legendRollover: track?.legendRollover,
              noImage: imageEntry?.noImage ?? true,
              hasDeepZoom: imageEntry?.hasDeepZoom ?? false,
              needsRegenerating: imageEntry?.needsRegenerating ?? false,
              depths: imageEntry?.depths,
              isCuttings: imageEntry?.isCuttings,
              useStrips: imageEntry?.useStrips ?? track?.useStrips ?? false
            };
          }

          if (trackType === 5 || trackType === 7 || trackType === 9) {
            const trackTypeId = Number(
              trackType === 5
                ? (track?.lithologyTypeId ?? track?.lithologyType ?? track?.trackTypeId)
                : trackType === 7
                  ? (track?.textTrackTypeId ?? track?.trackTypeId)
                  : (track?.stratigraphyTrackTypeId ?? track?.trackTypeId)
            );
            const entry = !isNaN(boreholeIdNum) && !isNaN(trackTypeId)
              ? groupTrackDataMap[boreholeIdNum]?.[trackTypeId]
              : undefined;
            const lexiconColorById = new Map<number, string>();
            if (trackType === 9 && Array.isArray(group?.stratigraphyLexiconStartAndEnds)) {
              group.stratigraphyLexiconStartAndEnds.forEach((item: any) => {
                const lexId = Number(item?.stratigraphyLexiconId);
                if (!isNaN(lexId) && item?.color) {
                  lexiconColorById.set(lexId, item.color);
                }
                (item?.alternativeIds || []).forEach((altId: any) => {
                  const altNum = Number(altId);
                  if (!isNaN(altNum) && item?.color) {
                    lexiconColorById.set(altNum, item.color);
                  }
                });
              });
            }
            const normalizedEntry = entry && trackType === 9
              ? {
                ...entry,
                data: (entry.data || []).map((d: any) => {
                  const lexId = Number(d?.stratigraphyLexiconId);
                  const fallbackValue = !isNaN(lexId) ? String(lexId) : undefined;
                  return {
                    ...d,
                    value: d?.value ?? fallbackValue,
                    backgroundColor: d?.backgroundColor ?? lexiconColorById.get(lexId)
                  };
                })
              }
              : entry;
            return {
              ...baseTrack,
              trackTypeId,
              lithologyTypeId: trackType === 5 ? trackTypeId : track?.lithologyTypeId,
              textTrackTypeId: trackType === 7 ? trackTypeId : track?.textTrackTypeId,
              stratigraphyTrackTypeId: trackType === 9 ? trackTypeId : track?.stratigraphyTrackTypeId,
              datas: normalizedEntry ? [normalizedEntry] : [],
              noData: normalizedEntry?.noData ?? true,
              codesAndColors: track?.codesAndColors || [],
              showGridlines: track?.showGridlines,
              showValueAxisAnnotation: track?.showValueAxisAnnotation,
              scaleType: track?.scaleType
            };
          }

          if (trackType === 0 || trackType === 3 || trackType === 4 || trackType === 8) {
            let curves: any[] = [];
            if (Array.isArray(track?.curves) && track.curves.length > 0) {
              curves = track.curves
                .map((curve: any) => {
                  const ttId = Number(curve?.trackTypeId);
                  if (isNaN(ttId)) return null;
                  const entry = !isNaN(boreholeIdNum) ? groupTrackDataMap[boreholeIdNum]?.[ttId] : undefined;
                  return {
                    ...curve,
                    trackTypeId: ttId,
                    boreholeId: !isNaN(boreholeIdNum) ? boreholeIdNum : undefined,
                    displayName: curve?.displayName || track?.displayName || track?.trackName,
                    noData: entry?.noData ?? true,
                    isAverage: entry?.isAverage ?? false,
                    scale: entry?.scale ?? null,
                    isCuttings: entry?.isCuttings ?? false
                  };
                })
                .filter(Boolean) as any[];
            } else {
              const ttId = Number(track?.trackTypeId);
              if (!isNaN(ttId)) {
                const entry = !isNaN(boreholeIdNum) ? groupTrackDataMap[boreholeIdNum]?.[ttId] : undefined;
                curves = [{
                  ...track,
                  trackTypeId: ttId,
                  boreholeId: !isNaN(boreholeIdNum) ? boreholeIdNum : undefined,
                  displayName: track?.displayName || track?.trackName,
                  noData: entry?.noData ?? true,
                  isAverage: entry?.isAverage ?? false,
                  scale: entry?.scale ?? null,
                  isCuttings: entry?.isCuttings ?? false
                }];
              }
            }

            const trackTypeIds = curves.map((c: any) => Number(c.trackTypeId)).filter((id: number) => !isNaN(id));
            const trackTrackDatas = trackTypeIds
              .map((id: number) => (!isNaN(boreholeIdNum) ? groupTrackDataMap[boreholeIdNum]?.[id] : undefined))
              .filter(Boolean) as any[];
            const hasData = trackTrackDatas.length > 0 && !trackTrackDatas.every((d: any) => d.noData);

            return {
              ...baseTrack,
              curves,
              label: curves.map((curve: any) => curve.displayName).join(', '),
              noData: !hasData,
              datas: trackTrackDatas,
              scale: trackTrackDatas[0]?.scale,
              isAverage: trackTrackDatas[0]?.isAverage,
              isCuttings: trackTrackDatas[0]?.isCuttings,
              minValue: trackTrackDatas[0]?.minValue,
              maxValue: trackTrackDatas[0]?.maxValue,
              showGridlines: track?.showGridlines,
              showValueAxisAnnotation: track?.showValueAxisAnnotation,
              scaleType: track?.scaleType,
              collapsed: track?.collapsed,
              isSelected: selectedTrack?.id === track?.id
            };
          }

          return null;
        })
        .filter(Boolean) as any[];

      const stableGroupId = String(
        group?.groupId ?? group?.id ?? (!isNaN(boreholeIdNum) ? boreholeIdNum : `group-${group?.id ?? Math.random()}`)
      );
      // Keep group header colors stable across useMemo re-runs:
      // - If Redux holds an explicit color (initial assignment or via updateGroupColor), adopt it.
      // - Otherwise fall back to a cached random color generated once on first render.
      if (group?.color) {
        groupColorCacheRef.current[stableGroupId] = group.color;
      } else if (!groupColorCacheRef.current[stableGroupId]) {
        groupColorCacheRef.current[stableGroupId] = getRandomColor();
      }

      return {
        groupId: stableGroupId,
        groupName: group?.groupName || group?.name || boreholeInfo?.name || `Borehole ${boreholeIdNum}`,
        color: groupColorCacheRef.current[stableGroupId],
        topOffset: group?.topOffset ?? 0,
        topOffsetType: group?.topOffsetType,
        maximumDepth: group?.maximumDepth ?? boreholeInfo?.maxDepth ?? maxDepth,
        boreholeId: !isNaN(boreholeIdNum) ? boreholeIdNum : undefined,
        collapsed: group?.collapsed ?? false,
        boreholeDoesNotContainStratigraphy: group?.boreholeDoesNotContainStratigraphy,
        stratigraphyLexiconStartAndEnds: group?.stratigraphyLexiconStartAndEnds,
        tracks: groupTracks
      };
    });
  }, [isViewMode, isMultiBoreholeMode, template, selectedBoreholeIds, groupTrackImages, groupTrackDataMap, boreholeOptions, viewData, maxDepth, selectedTrack, isLoggedDepthTypeSelected]);

  const noDataTrackTypeIdSet = useMemo(() => new Set(noDataTrackTypeIds), [noDataTrackTypeIds]);
  const noDataFromHierarchySet = useMemo(() => {
    const noDataIds = trackTypeOptions
      .filter((t: any) => t.dataStatus === 'no-data')
      .map((t: any) => Number(t.id))
      .filter((id: number) => !isNaN(id));
    return new Set(noDataIds);
  }, [trackTypeOptions]);

  const combinedNoDataSet = useMemo(() => {
    const combined = new Set<number>();
    noDataTrackTypeIdSet.forEach((id) => combined.add(id));
    noDataFromHierarchySet.forEach((id) => combined.add(id as number));
    return combined;
  }, [noDataTrackTypeIdSet, noDataFromHierarchySet]);

  const availableTrackTypeIds = useMemo(
    () => trackTypeIds.filter((id) => scaleMetadataMap[id] && !combinedNoDataSet.has(id)),
    [trackTypeIds, scaleMetadataMap, combinedNoDataSet]
  );

  const missingTrackTypeIds = useMemo(
    () => trackTypeIds.filter((id) => !scaleMetadataMap[id] && !combinedNoDataSet.has(id)),
    [trackTypeIds, scaleMetadataMap, combinedNoDataSet]
  );

  const scopedMissingTrackTypeIds = useMemo(() => {
    if (addedTrackTypeIds.length === 0) return missingTrackTypeIds;
    const addedSet = new Set(addedTrackTypeIds);
    return missingTrackTypeIds.filter((id) => addedSet.has(id));
  }, [missingTrackTypeIds, addedTrackTypeIds]);

  const scopedAvailableTrackTypeIds = useMemo(() => {
    if (addedTrackTypeIds.length === 0) return availableTrackTypeIds;
    const addedSet = new Set(addedTrackTypeIds);
    return availableTrackTypeIds.filter((id) => addedSet.has(id));
  }, [availableTrackTypeIds, addedTrackTypeIds]);

  useEffect(() => {
    if (!boreholeData?.depthTypes?.[depthType]) return;
    if (startDepth == null || endDepth == null) return;

    const minDepth = boreholeData.depthTypes[depthType]?.minDepth ?? 0;
    const maxDepth = boreholeData.depthTypes[depthType]?.maxDepth ?? 0;
    const tolerance = 0.001;
    const isFullRange = Math.abs(startDepth - minDepth) <= tolerance && Math.abs(endDepth - maxDepth) <= tolerance;
    if (!isFullRange) return;

    const emptyIds = (trackData || [])
      .filter((t: any) => {
        const dataArray = t?.data || t?.datas || [];
        return dataArray.length === 0;
      })
      .map((t: any) => t.trackTypeId)
      .filter((id: any) => typeof id === 'number');

    if (emptyIds.length === 0) return;
    setNoDataTrackTypeIds((prev) => Array.from(new Set([...prev, ...emptyIds])));
  }, [trackData, boreholeData, depthType, startDepth, endDepth]);

  const depthRange = useMemo(() => {
    if (startDepth == null || endDepth == null) return null;
    return endDepth - startDepth;
  }, [startDepth, endDepth]);

  const computedEntries = useMemo(() => {
    if (!depthRange || depthRange <= 0) return [] as Array<{ trackTypeId: number; scale: number }>;
    if (!availableTrackTypeIds.length) return [] as Array<{ trackTypeId: number; scale: number }>;
    return computeOptimalResolutionsForTrackTypes(
      availableTrackTypeIds,
      scaleMetadataMap as any,
      depthRange,
      2000,
      cumulativeGroups
    );
  }, [availableTrackTypeIds, scaleMetadataMap, depthRange, cumulativeGroups]);

  useEffect(() => {
    if (isViewMode) return;
    if (!clientId || !projectId) return;
    if (startDepth == null || endDepth == null) return;
    // When adding boreholes in view mode, we dispatch a targeted fetch directly
    if (isViewMode && addedBoreholeIds.length > 0) return;
    const hasAnyTrackTypeIds = trackTypeIds.length > 0 || Object.values(multiTrackTypeIdsByBorehole).some((ids) => (ids || []).length > 0);
    if (!hasAnyTrackTypeIds && !imageTypeIds.length) return;

    const depthRange = endDepth - startDepth;
    if (depthRange <= 0) return;

    const targetBoreholeIds = isViewMode && !hasConfirmedBoreholes && viewBoreholeIds.length > 0
      ? viewBoreholeIds
      : (isMultiBoreholeMode
          ? (addedBoreholeIds.length > 0 ? addedBoreholeIds : selectedBoreholeIds)
          : (boreholeId ? [Number(boreholeId)] : []));

    const boreholesPayload = targetBoreholeIds.map((bId: number) => {
      const allTrackIds = multiTrackTypeIdsByBorehole[bId] || trackTypeIds;
      let scopedTrackIds: number[] = [];
      const isTemplateAddedBorehole = !isViewMode && addedBoreholeIds.includes(bId);
      
      if (addedTrackTypeIds.length > 0) {
        scopedTrackIds = addedTrackTypeIds.slice();
      } else if (isViewMode && addedBoreholeIds.includes(bId) && commonViewTrackTypeIds.length > 0) {
        // For newly added boreholes, use common track types from existing boreholes
        scopedTrackIds = commonViewTrackTypeIds.slice();
      } else {
        // For existing boreholes, use their own track types
        scopedTrackIds = allTrackIds.slice();
      }
      // When explicitly adding tracks, bypass the no-data filter for those IDs so
      // we always attempt to fetch them regardless of previous no-data state.
      if (addedTrackTypeIds.length > 0) {
        const addedSet = new Set(addedTrackTypeIds);
        scopedTrackIds = scopedTrackIds.filter((id) => addedSet.has(id) || !combinedNoDataSet.has(id));
      } else if (isTemplateAddedBorehole) {
        // No-data flags from the primary borehole should not block the first fetch
        // for a newly added borehole in template correlation mode.
        scopedTrackIds = scopedTrackIds.slice();
      } else {
        scopedTrackIds = scopedTrackIds.filter((id) => !combinedNoDataSet.has(id));
      }

      if (scopedTrackIds.length === 0 && imageTypeIds.length === 0) return null;
      return {
        boreholeId: bId,
        projectId,
        trackTypeIds: scopedTrackIds,
        imageTypeIds
      };
    }).filter(Boolean) as Array<{ boreholeId: number; projectId: string; trackTypeIds: number[]; imageTypeIds: number[] }>;

    if (boreholesPayload.length === 0) return;

    const unifiedKey = JSON.stringify({
      clientId,
      projectId,
      boreholes: boreholesPayload.map((b) => ({
        boreholeId: b.boreholeId,
        trackTypeIds: b.trackTypeIds.slice().sort((a, b) => a - b).join(','),
        imageTypeIds: b.imageTypeIds.slice().sort((a, b) => a - b).join(',')
      })),
      startDepth,
      endDepth,
      pixelHeight: viewportHeight
    });

    if (unifiedKey === lastUnifiedFetchRef.current || unifiedKey === lastUnifiedFetchKey || isUnifiedFetchInFlight) return;

    // If the depth range grew but not enough to warrant re-fetching scales/data, skip this
    // run but stamp the new key so subsequent runs with the same range are also skipped.
    if (skipNextUnifiedFetchRef.current) {
      skipNextUnifiedFetchRef.current = false;
      lastUnifiedFetchRef.current = unifiedKey;
      lastUnifiedFetchKey = unifiedKey;
      return;
    }

    lastUnifiedFetchRef.current = unifiedKey;
    lastUnifiedFetchKey = unifiedKey;
    isUnifiedFetchInFlight = true;

    // Decide whether to skip the getScaleMetadata round-trip.
    // Skip it when the depth range is within [0.5×, 2×] of the last scale-fetched range —
    // the optimal resolution is very unlikely to have changed for minor panning / small zooms.
    // Always fetch full scales the first time (lastScaleFetchedRangeRef is null).
    const currentDepthRange = endDepth - startDepth;
    const lastScaleRange = lastScaleFetchedRangeRef.current;
    const skipScaleCheck = lastScaleRange !== null
      && (currentDepthRange / lastScaleRange) > 0.5
      && (currentDepthRange / lastScaleRange) < 2.0;
    if (!skipScaleCheck) {
      lastScaleFetchedRangeRef.current = currentDepthRange;
    }

    setLastMultiTrackRequest({
      boreholeEntries: boreholesPayload,
      bufferedStart: startDepth,
      bufferedEnd: endDepth,
      pixelHeight: viewportHeight
    });
    setLastMultiTrackResponse(null);

    if (isViewMode) {
      fetchViewScalesAndDataDirect({
        boreholes: boreholesPayload,
        startDepth,
        endDepth,
        skipScaleCheck
      }).finally(() => {
        isUnifiedFetchInFlight = false;
      });
    } else if (addedTrackTypeIds.length > 0) {
      // Bypass the Redux thunk pipeline for newly added tracks – call the API directly
      // (mirrors the view-mode direct fetch approach)
      fetchViewScalesAndTrackDataDirect({
        clientId,
        boreholes: boreholesPayload,
        startDepth,
        endDepth,
        pixelHeight: viewportHeight,
        depthUnit: template?.depthUnit ?? null,
        cumulativeGroups,
        skipScaleCheck
      }).then((result) => {
        dispatch(mergeDirectTrackData({
          groupTrackData: result.groupTrackData,
          scaleMetadataByBorehole: result.scaleMetadataByBorehole,
          groupTrackImages: result.groupTrackImages
        }));
        // Depth range extension is handled centrally by the templateMinDepth/templateMaxDepth useEffect
      }).finally(() => {
        isUnifiedFetchInFlight = false;
      });
    } else {
      dispatch(fetchScalesAndTrackData({
        clientId,
        projectId,
        boreholes: boreholesPayload,
        pixelHeight: viewportHeight,
        byBoreholeDepth: true,
        startDepth,
        endDepth,
        depthUnit: template?.depthUnit ?? null,
        cumulativeGroups,
        skipScaleCheck
      })).then((action: any) => {
        const payload = action?.payload?.payload || action?.payload;
        if (!payload?.trackData) return;
        const summary = (payload.trackData || []).reduce((acc: any, item: any) => {
          const boreholeIdNum = Number(item?.boreholeId);
          const trackTypeIdNum = Number(item?.trackTypeId);
          if (isNaN(boreholeIdNum) || isNaN(trackTypeIdNum)) return acc;
          if (!acc[boreholeIdNum]) acc[boreholeIdNum] = [];
          if (!acc[boreholeIdNum].includes(trackTypeIdNum)) acc[boreholeIdNum].push(trackTypeIdNum);
          return acc;
        }, {} as { [boreholeId: number]: number[] });
        setLastMultiTrackResponse({
          summary,
          count: payload.trackData?.length || 0
        });
        // Depth range extension is handled centrally by the templateMinDepth/templateMaxDepth useEffect
      }).finally(() => {
        isUnifiedFetchInFlight = false;
      });
    }

    if (addedBoreholeIds.length > 0) {
      setAddedBoreholeIds([]);
    }
    if (addedTrackTypeIds.length > 0) {
      setAddedTrackTypeIds([]);
    }
  }, [
    clientId,
    projectId,
    boreholeId,
    isMultiBoreholeMode,
    isViewMode,
    hasConfirmedBoreholes,
    selectedBoreholeIds,
    addedBoreholeIds,
    addedTrackTypeIds,
    trackTypeIds,
    imageTypeIds,
    multiTrackTypeIdsByBorehole,
    combinedNoDataSet,
    commonViewTrackTypeIds,
    viewBoreholeIds,
    startDepth,
    endDepth,
    viewportHeight,
    template?.depthUnit,
    fetchViewScalesAndDataDirect,
    dispatch
  ]);
  return (
    <>
      <LeftSidePanel
        showLeftSidePanel={panelLogic.showLeftSidePanel}
        showDepthPanel={panelLogic.showDepthPanel}
        showXAxisPanel={panelLogic.showXAxisPanel}
        showPrintPanel={panelLogic.showPrintPanel}
        showTemplatesPanel={panelLogic.showTemplatesPanel}
        showViewsPanel={panelLogic.showViewsPanel}
        showInfoPanel={panelLogic.showInfoPanel}
        showExportPanel={panelLogic.showExportPanel}
        isViewMode={isViewMode}
        isMultiBoreholeMode={isMultiBoreholeMode}
        depthType={depthType}
        onChangeDepthType={panelLogic.onChangeDepthType}
        depthUnit={depthUnit}
        changeDepthUnit={panelLogic.onChangeDepthUnit}
        childLoading={false}
        setStartDepth={panelLogic.onSetStartDepth}
        setEndDepth={panelLogic.onSetEndDepth}
        minDepth={minDepth}
        maxDepth={maxDepth}
        setSkipLoad={panelLogic.setSkipLoad}
        showDepthGridLines={template?.showDepthGridLines}
        majorIntervals={template?.depthMajorIntervals}
        setMajorIntervals={(value: number | undefined) => panelLogic.onSetDepthMajorIntervals(value ?? 0)}
        setShowDepthGridlines={panelLogic.onSetShowDepthGridlines}
        showDepthMinorIntervals={template?.showDepthMinorIntervals}
        setShowDepthMinorIntervals={panelLogic.onSetShowDepthMinorIntervals}
        minorIntervals={template?.depthMinorIntervals}
        setMinorIntervals={(value: number | undefined) => panelLogic.onSetDepthMinorIntervals(value ?? 0)}
        setManualDepthRange={setManualDepthRange}
        startDepth={startDepth}
        endDepth={endDepth}
        hasLoggedDepth={hasLoggedDepth}
        showValueAxisAnnotation={showValueAxisAnnotation}
        setShowValueAxisAnnotation={panelLogic.onSetShowValueAxisAnnotation}
        currentTemplate={template}
        currentTemplateLevel={currentTemplateLevel}
        moveTemplateLevel={moveTemplateLevel}
        moveTemplateError={moveTemplateError}
        builtInTemplateOptions={builtInTemplateOptions}
        userTemplateOptions={userTemplateOptions}
        boreholeTemplateOptions={boreholeTemplateOptions}
        projectTemplateOptions={projectTemplateOptions}
        clientTemplateOptions={clientTemplateOptions}
        onChangeBuiltInTemplate={onChangeBuiltInTemplate}
        onChangeUserTemplate={onChangeUserTemplate}
        onChangeBoreholeTemplate={onChangeBoreholeTemplate}
        onChangeProjectTemplate={onChangeProjectTemplate}
        onChangeClientTemplate={onChangeClientTemplate}
        selectedTemplateId={selectedTemplateId ?? activeTemplateId}
        templateLoading={templateLoading}
        onSaveTemplate={onSaveTemplate}
        canSaveTemplate={canSaveTemplate}
        canDeleteTemplate={canDeleteTemplate}
        templateNotSaved={templateNotSaved}
        templateSaveError={templateSaveError}
        onSaveTemplateAs={onSaveTemplateAs}
        onDeleteTemplate={onDeleteTemplate}
        onNewTemplate={onNewTemplate}
        templateLevels={templateLevels}
        mouseWheelZoom={mouseWheelZoom}
        setMouseWheelZoom={setMouseWheelZoom}
        currentView={viewData}
        currentViewLevel={currentViewLevel}
        moveViewLevel={moveViewLevel}
        moveViewError={moveViewError}
        viewsLoading={viewsLoading}
        userViewOptions={userViewOptions}
        clientViewOptions={clientViewOptions}
        onChangeUserView={onChangeUserView}
        onChangeClientView={onChangeClientView}
        selectedViewId={selectedViewId}
        onSaveView={onSaveView}
        canSaveView={canSaveView}
        canDeleteView={canDeleteView}
        viewNotSaved={viewNotSaved}
        onSaveViewAs={onSaveViewAs}
        onNewView={onNewView}
        metaDataDepth={metaDataDepth}
        latitude={boreholeData?.latitude}
        longitude={boreholeData?.longitude}
        loggingStarted={boreholeData?.loggingStarted}
        loggingEnded={boreholeData?.loggingEnded}
        loggingTeam={boreholeData?.loggingTeam}
        comments={boreholeData?.comments}
        status={boreholeData?.status}
        equipment={boreholeData?.equipment}
        infoImage={null}
        handleOkPrintModal={panelLogic.handleOkPrintModal}
        isPrinting={panelLogic.isPrinting}
        paperSizes={panelLogic.paperSizes}
        printError={panelLogic.printError}
        printProgress={panelLogic.printProgress}
        showSummaryReport={showSummaryReport}
        statisticsTracks={template?.statisticsTracks || []}
        dataForDepthRangeOptions={panelLogic.dataForDepthRangeOptions}
        isExporting={panelLogic.isExporting}
        setIsExporting={panelLogic.setIsExporting}
        exportFile={panelLogic.onExportFile}
        exportError={panelLogic.exportError}
        exportProgress={panelLogic.exportProgress}
        exportFileName={panelLogic.exportFileName}
        setExportFileName={panelLogic.setExportFileName}
        exportDownloaded={panelLogic.exportCompleted}
        exportUrl={panelLogic.exportUrl}
        showExportTemplates={false}
        showSaveAsModal={showSaveAsModal}
        showSaveViewAsModal={showSaveViewAsModal}
        saveTemplateName={saveTemplateName}
        setSaveTemplateName={setSaveTemplateName}
        saveTemplateNameError={saveTemplateNameError}
        saveAsTemplateLevel={saveAsTemplateLevel}
        setSaveAsTemplateLevel={setSaveAsTemplateLevel}
        saveViewName={saveViewName}
        setSaveViewName={setSaveViewName}
        saveViewNameError={saveViewNameError}
        saveAsViewLevel={saveAsViewLevel}
        setSaveAsViewLevel={setSaveAsViewLevel}
        onShowSaveTemplateDialog={onShowSaveTemplateDialog}
        onShowSaveViewDialog={onShowSaveViewDialog}
        handleCloseSaveAsModal={handleCloseSaveAsModal}
        handleOkSaveAsModal={handleOkSaveAsModal}
        handleCloseSaveViewAsModal={handleCloseSaveViewAsModal}
        handleOkSaveViewAsModal={handleOkSaveViewAsModal}
      />

      <LeftSideMenu
        showLeftSidePanel={panelLogic.showLeftSidePanel}
        onShowHideLeftSidePanel={panelLogic.onToggleLeftSidePanel}
        showDepthPanel={panelLogic.showDepthPanel}
        onShowDepthPanel={panelLogic.onShowDepthPanel}
        showXAxisPanel={panelLogic.showXAxisPanel}
        onShowXAxisPanel={panelLogic.onShowXAxisPanel}
        showPrintPanel={panelLogic.showPrintPanel}
        onShowPrintPanel={panelLogic.onShowPrintPanel}
        showTemplatesPanel={panelLogic.showTemplatesPanel}
        onShowTemplatesPanel={panelLogic.onShowTemplatesPanel}
        showViewsPanel={panelLogic.showViewsPanel}
        onShowViewsPanel={panelLogic.onShowViewsPanel}
        notSaved={templateNotSaved}
        showInfoPanel={panelLogic.showInfoPanel}
        onShowInfoPanel={panelLogic.onShowInfoPanel}
        showExportPanel={panelLogic.showExportPanel}
        onShowExportPanel={panelLogic.onShowExportPanel}
        showView={isViewMode || isMultiBoreholeMode}
        templateSelected={Boolean(template?.tracks?.length || viewData?.groups?.length)}
        templateLoaded={templatesLoaded}
        viewNotSaved={viewNotSaved}
      />

      <div style={{ marginLeft: panelLogic.showLeftSidePanel ? 350 : 50, marginRight: showRightSidePanel ? 427 : 0 }} className="sidebar-main">
        <div className="level log-viewer-title">
          <div className="level-left">
            <div className="level-item">
              <h2 className="mt-2">{isViewMode || isMultiBoreholeMode ? "Correlation Viewer" : boreholeData?.name}</h2>
            </div>
            <div className="level-item">
              <DepthData depthUnit={depthUnit} metaDataDepth={metaDataDepth}/>                
            </div>
          </div>
        </div>
        <section>
        <div>          
          {(templateLoading || (isViewMode ? directLoadingHighResolution : (loadingHighResolution || scalesLoading || trackDataLoading))) && <Loader />}
          {showCharts && (
            <TrackData
              clientId={clientId}
              projectId={projectId}
              displayTrackInstances={displayTrackInstances}
              displayImageTrackInstances={displayImageTrackInstances}
              trackTypeNameById={trackTypeNameById}
              scaleMetadataMap={scaleMetadataMap}
              groupTrackDataMap={groupTrackDataMap}
              trackTypeIds={trackTypeIds}
              imageTypeIds={imageTypeIds}
              trackData={trackData}
              groupTrackData={effectiveGroupTrackData}
              trackImages={trackImages}
              groupTrackImages={effectiveGroupTrackImages}
              template={template}
              viewData={isViewMode || isMultiBoreholeMode ? viewData : undefined}
              view={isViewMode || isMultiBoreholeMode ? viewData : undefined}
              isViewMode={isViewMode}
              showView={isViewMode || isMultiBoreholeMode}
              startDepth={startDepth}
              endDepth={endDepth}
              minDepth={minDepth}
              maxDepth={maxDepth}
              depthUnit={depthUnit}
              byBoreholeDepth={!isLoggedDepthTypeSelected}
              depthType={depthType}
              setStartDepth={setStartDepth}
              setEndDepth={setEndDepth}
              viewportHeight={viewportHeight}
              boreholeId={boreholeId}
              setMetaDataDepth={setMetaDataDepth}
              depthMajorIntervals={template?.depthMajorIntervals ?? depthMajorIntervals}
              setDepthMajorIntervals={panelLogic.onSetDepthMajorIntervals}
              showDepthGridLines={template?.showDepthGridLines}
              setShowDepthGridlines={panelLogic.onSetShowDepthGridlines}
              depthMinorIntervals={template?.depthMinorIntervals ?? depthMinorIntervals}
              setDepthMinorIntervals={panelLogic.onSetDepthMinorIntervals}
              showDepthMinorIntervals={template?.showDepthMinorIntervals}
              setShowDepthMinorIntervals={panelLogic.onSetShowDepthMinorIntervals}
              setPauseUpdate={setPauseUpdate}
              setManualDepthRange={setManualDepthRange}
              editMode={false}
              scalesLoaded={isViewMode ? directScalesLoaded : scalesLoaded}
              trackDataLoading={isViewMode ? directLoadingHighResolution : loadingHighResolution}
              updateCurve={updateCurve}
              updateCurves={onUpdateCurves}
              trackHeaderClick={onTrackHeaderClick}
              onSetSelectedTrack={onSetSelectedTrack}
              onSetTrackCollapsed={onSetTrackCollapsed}
              onUpdateCurves={onUpdateCurves}
              onSetShowGridlines={onSetShowGridlines}
              onSetScaleType={onSetScaleType}
              onAddCurve={onAddCurve}
              onRemoveTrack={onRemoveTrack}
              selectLithology={onSelectLithology}
              addLithology={onAddLithology}
              onChangeDataValue={onChangeDataValue}
              onAddDataValue={onAddDataValue}
              onDeleteDataValue={onDeleteDataValue}
              onSaveLithologyData={onSaveLithologyData}
              onChangeTextData={onChangeTextData}
              onAddTextValue={onAddTextValue}
              onSaveTextData={onSaveTextData}
              onChangeStratigraphyData={onChangeStratigraphyData}
              onAddStratigraphyValue={onAddStratigraphyValue}
              onSaveChangeStratigraphyData={onSaveChangeStratigraphyData}
              onChangeDepth={onChangeDepth}
              onSetTrackEditMode={onSetTrackEditMode}
              onUpdateTrackOrder={onUpdateTrackOrder}
              onUpdateGroupTrackOrder={onUpdateGroupTrackOrder}
              onUpdateGroupOrder={onUpdateGroupOrder}
              collapseGroup={onCollapseGroup}
              selectedTrack={selectedTrack}
              selectedGroupId={selectedGroupId}
              sortedTrackData={sortedTrackData}
              sortedGroupTrackData={viewSortedGroupTrackData}
              viewSortedGroupTrackData={viewSortedGroupTrackData}
              trackOrderData={trackOrderData}
              deletedCurves={deletedCurves}
              mouseWheelZoom={mouseWheelZoom}
            />
          )}
        </div>
        </section>
      </div>

      <RightSideMenu
        showRightSidePanel={showRightSidePanel}
        showAddTrackPanel={showAddTrackPanel}
        showReorderPanel={showReorderPanel}
        showAdjustmentsPanel={showAdjustmentsPanel}
        showAddStatisticsPanel={showAddStatisticsPanel}
        showCorrelationPanel={showCorrelationPanel}
        showSummaryReport={showSummaryReport}
        onShowHideAdjustmentsPanel={onShowHideAdjustmentsPanel}
        onShowAddTrackPanel={onShowAddTrackPanel}
        onShowReorderPanel={onShowReorderPanel}
        onShowAdjustments={onShowAdjustments}
        onShowAddStatistics={onShowAddStatistics}
        onShowCorrelationPanel={onShowCorrelationPanel}
      />

      <RightSidePanel
        showRightSidePanel={showRightSidePanel}
        showAddTrackPanel={showAddTrackPanel}
        showReorderPanel={showReorderPanel}
        showAdjustmentsPanel={showAdjustmentsPanel}
        showCorrelationPanel={showCorrelationPanel}
        showAddStatisticsPanel={showAddStatisticsPanel}
        statisticsTracks={template?.statisticsTracks || []}
        onAddStatisticalTrack={onAddStatisticalTrack}
        onDeleteStatisticalTrack={onDeleteStatisticalTrack}
        onAddTrack={onAddTrack}
        selectedTrackType={selectedTrackType}
        setSelectedTrackType={setSelectedTrackType}
        selectedTrackTypeIds={selectedTrackTypeIds}
        setSelectedTrackTypeIds={setSelectedTrackTypeIds}
        selectedTemplateTrackType={selectedTemplateTrackType}
        setSelectedTemplateTrackType={setSelectedTemplateTrackType}
        allowedTrackCategories={addTrackAllowedCategories}
        checkedImageTypes={checkedImageTypes}
        setCheckedImageTypes={setCheckedImageTypes}
        checkedLithologyTypes={checkedLithologyTypes}
        setCheckedLithologyTypes={setCheckedLithologyTypes}
        checkedStratigraphyTypes={checkedStratigraphyTypes}
        setCheckedStratigraphyTypes={setCheckedStratigraphyTypes}
        checkedTextTypes={checkedTextTypes}
        setCheckedTextTypes={setCheckedTextTypes}
        imageTypes={imageTypes?.imageTypes || []}
        selectedChartCategory={selectedChartCategory}
        setSelectedChartCategory={setSelectedChartCategory}
        trackTypesHierarchyData={trackTypesHierarchyData}
        trackTypesHierarchyLoading={trackTypesHierarchyLoading}
        onHideAddTrackPanel={onHideAddTrackPanel}
        sortedTrackData={sortedTrackData}
        trackOrderData={trackOrderData}
        onUpdateTrackOrder={onUpdateTrackOrder}
        onUpdateGroupTrackOrder={onUpdateGroupTrackOrder}
        onUpdateGroupOrder={onUpdateGroupOrder}
        isViewMode={isViewMode}
        viewSortedGroupTrackData={viewSortedGroupTrackData}
        selectedTrack={selectedTrack}
        setSelectedTrack={onSetSelectedTrack}
        selectedGroupId={selectedGroupId}
        setTrackCollapsed={onSetTrackCollapsed}
        updateCurves={onUpdateCurves}
        setShowGridlines={onSetShowGridlines}
        setScaleType={onSetScaleType}
        deleteTrack={onRemoveTrack}
        addCurve={onAddCurve}
        clientId={clientId}
        onChangeDataValue={onChangeDataValue}
        onAddDataValue={onAddDataValue}
        onDeleteDataValue={onDeleteDataValue}
        onSaveLithologyData={onSaveLithologyData}
        onChangeTextData={onChangeTextData}
        onAddTextValue={onAddTextValue}
        onSaveTextData={onSaveTextData}
        onChangeStratigraphyData={onChangeStratigraphyData}
        onAddStratigraphyValue={onAddStratigraphyValue}
        onSaveChangeStratigraphyData={onSaveChangeStratigraphyData}
        onChangeDepth={onChangeDepth}
        onSetTrackEditMode={onSetTrackEditMode}
        boreholesByProject={boreholesByProject}
        projectId={projectId}
        boreholeId={boreholeId}
        onAddBoreholes={onAddBoreholes}
        viewportHeight={viewportHeight}
        startDepth={startDepth}
        endDepth={endDepth}
        depthUnit={depthUnit}
        boreholeTextTrackData={boreholeTextTrackData}
        boreholeStratigraphyTrackData={boreholeStratigraphyTrackData}
      />
      <NotSavedDialog
        showNotSavedModal={showNotSavedModal}
        handleCloseNotSavedModal={handleCloseNotSavedModal}
        handleYesNotSavedModal={handleYesNotSavedModal}
        handleNoNotSavedModal={handleNoNotSavedModal}
      />
    </>
  );
};

export default BoreholeLogViewerV2;
